import React from 'react';
import * as Headless from '@headlessui/react';
import {
  Form,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutlet,
  useParams,
  useNavigation,
  useSearchParams,
  useSubmit
} from 'react-router-dom';

import { getApiBaseUrl } from '@oysterjs/core/config';
import { Await } from '@oysterjs/ui/async';
import {
  Action,
  AppliedEpicIntegrationType,
  CreateIntegrationInput,
  CreateIntegrationMutation,
  GetIntegrationDocument,
  GetIntegrationQuery,
  IntegrationConfiguration,
  IntegrationDetailFieldsFragment,
  IntegrationFieldsFragment,
  IntegrationState,
  IntegrationSyncLogFieldsFragment,
  IntegrationSyncState,
  IntegrationType,
  HubSpotCallSyncType,
  HubSpotTaskSyncType,
  LoggedInAgencyUserFieldsFragment,
  SyncIntegrationDocument,
  SyncIntegrationMutation,
  useUpdateIntegrationMutation,
  useGetHubSpotTicketPipelinesQuery,
  useGetIntegrationStateQuery,
  useGetIntegrationsQuery,
  Ams360TaskAssignmentMethod,
  QqCatalystTaskAssignment,
  QqCatalystCategoryCount,
  UpdateAms360IntegrationInput,
  UpdateAppliedEpicIntegrationInput,
  UpdateIntegrationInput,
  Resource
} from '~/api/graphql';
import { Spinner } from '@oysterjs/ui/spinner';
import { AnimatePresence } from 'motion/react';
import { Button, PrimaryButton } from '@oysterjs/ui/button';
import { Badge } from '@oysterjs/ui/badge';
import { PermissionButton, PermissionNotice, usePermissions } from '~/components/permissions';
import clsx from 'clsx';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { ValidationError } from '@oysterjs/core/graphql';
import { getValidationErrors, messageFromError } from '@oysterjs/core/graphql/errors';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions, getValidationError } from '@oysterjs/core/graphql/form';
import { client } from '~/api/client';
import { ApolloError } from '@apollo/client';
import { CheckIcon, EyeIcon, EyeSlashIcon, ClipboardIcon } from '@heroicons/react/24/solid';
import { Combobox } from '@oysterjs/ui/combobox';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ProLabel } from '~/components/pro-label';
import { ProUpgradeDialog } from '~/components/pro-upgrade-dialog';
import { useAmsCrmIntegrationsEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { INTEGRATIONS_PRO_UPGRADE_USE_CASES } from '~/components/settings/integrations-upgrade';
import { useAmsLoggingEnabled } from '~/components/use-ams-logging-enabled';
import { SentEmailLoggingSection } from './sent-email-logging-switch';
import { SentEmailLoggingSettings } from './sent-email-logging-settings';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const integrations: Array<{
  name: string;
  type?: IntegrationType;
  category: string;
  comingSoon: boolean;
  tags: string[];
  icon: string;
}> = [
  {
    name: 'Hawksoft',
    type: IntegrationType.AmsHawksoft,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://media.licdn.com/dms/image/v2/C4E0BAQFf1O-K5VVDTQ/company-logo_200_200/company-logo_200_200/0/1630652782314/hawksoft_inc_logo?e=2147483647&v=beta&t=vob2LV6WEJjB6IQ29IjOoRvJG1Hq3oSEVbCKa7ZTEh4'
  },
  {
    name: 'HubSpot',
    type: IntegrationType.CrmHubspot,
    category: 'CRM',
    comingSoon: false,
    tags: ['Clients', 'Tickets', 'Emails', 'Tasks', 'Calls'],
    icon: 'https://cdn4.iconfinder.com/data/icons/logos-and-brands/512/168_Hubspot_logo_logos-512.png'
  },
  {
    name: 'Zapier',
    type: IntegrationType.Zapier,
    category: 'Automation',
    comingSoon: false,
    tags: ['Workflows'],
    icon: 'https://cdn.freebiesupply.com/logos/large/2x/zapier-logo-svg-vector.svg'
  },
  {
    name: 'Applied Epic',
    type: IntegrationType.AmsAppliedEpic,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://media-s3-us-east-1.ceros.com/applied-systems/images/2020/02/06/a6c5b28a3c58abd79f67017a848c1b8f/asset-9.png'
  },
  {
    name: 'AMS360',
    type: IntegrationType.Ams_360,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTa7wS-qfS1JyfoZ2Mbri8yMGh4HMeP52-LGw&s'
  },
  {
    name: 'NowCerts Momentum',
    type: IntegrationType.AmsNowcerts,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://media.licdn.com/dms/image/v2/D560BAQFA33dds1mSpA/company-logo_200_200/company-logo_200_200/0/1731698561679/momentumamp_logo?e=2147483647&v=beta&t=GHkVoJy3eOXGQS1qJPJQ_oxJPIfQhxjqFqAq4JZP640'
  },
  {
    name: 'AgencyZoom',
    type: IntegrationType.CrmAgencyZoom,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Tasks'],
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTa7wS-qfS1JyfoZ2Mbri8yMGh4HMeP52-LGw&s'
  },
  {
    name: 'Salesforce',
    category: 'CRM',
    comingSoon: false,
    tags: ['Clients', 'Tickets', 'Emails'],
    icon: 'https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg'
  },
  {
    name: 'EZLynx',
    type: IntegrationType.AmsEzlynx,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://media.licdn.com/dms/image/v2/D4E0BAQE5DofRfjrq4g/company-logo_200_200/company-logo_200_200/0/1705671105156/ezlynx_logo?e=2147483647&v=beta&t=006dAyH8Lrc3-b9kdvuYzVYkzjI0z32pBR9Uai6-v_s'
  },
  {
    name: 'ImageRight',
    type: IntegrationType.AmsImageright,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTa7wS-qfS1JyfoZ2Mbri8yMGh4HMeP52-LGw&s'
  },
  {
    name: 'QQCatalyst',
    type: IntegrationType.AmsQqcatalyst,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Prospects', 'Policies'],
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTa7wS-qfS1JyfoZ2Mbri8yMGh4HMeP52-LGw&s'
  }
];

const CREATE_INTEGRATION = [{ resource: Resource.Integration, action: Action.Create }];
const UPDATE_INTEGRATION = [{ resource: Resource.Integration, action: Action.Update }];
const SYNC_INTEGRATION = [{ resource: Resource.Integration, action: Action.Sync }];

const IntegrationConnectAction = (props: {
  integration: (typeof integrations)[number];
  connected?: IntegrationFieldsFragment;
  canConnectIntegrations: boolean;
  onUpgradeClick: () => void;
}) => {
  if (props.integration.comingSoon) {
    return (
      <Button color="neutral" disabled className="w-full data-[disabled]:cursor-not-allowed">
        Coming Soon
      </Button>
    );
  }

  if (props.connected) {
    return (
      <div className="flex items-center gap-2">
        <Button
          color="white"
          className="w-full"
          href={`/app/settings/integrations/manage/${props.connected.id}`}
        >
          Manage
        </Button>
      </div>
    );
  }

  if (!props.integration.type) {
    return null;
  }

  if (props.canConnectIntegrations) {
    return (
      <PermissionButton
        permissions={CREATE_INTEGRATION}
        color="sky"
        className="w-full"
        href={`/app/settings/integrations/connect/${props.integration.type}`}
      >
        Connect
      </PermissionButton>
    );
  }

  return (
    <Button color="sky" className="w-full" onClick={props.onUpgradeClick}>
      Upgrade to Pro
    </Button>
  );
};

export const SettingsIntegrationsPage = () => {
  const canConnectIntegrations = useAmsCrmIntegrationsEntitlement();
  const [showProUpgradeDialog, setShowProUpgradeDialog] = React.useState(false);
  const isAmsLoggingEnabled = useAmsLoggingEnabled();
  const outlet = useOutlet();
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUser: LoggedInAgencyUserFieldsFragment;
      integrations: IntegrationFieldsFragment[];
    }>;
  };

  return (
    <>
      <React.Suspense fallback={<IntegrationsPageLoader />}>
        <Await resolve={loader.data}>
          {(data) => {
            if (!data?.agencyUser?.agency.subscription) {
              return null;
            }

            const connectedByType = new Map(
              (data.integrations ?? []).map((integration) => [integration.type, integration])
            );
            const emailLoggingIntegration = isAmsLoggingEnabled
              ? data.integrations?.find((connected) => connected.logsSentEmails)
              : undefined;

            return (
              <div className="space-y-6">
                {!outlet && <QQCatalystOutcomeBanner />}
                <IntegrationStatusBanner canConnectIntegrations={canConnectIntegrations} />
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg overflow-hidden bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
                        Integrations
                      </h3>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-[1px] sm:grid-cols-2 xl:grid-cols-3 bg-neutral-200 dark:bg-neutral-700">
                    {integrations
                      .sort((a, b) => {
                        const aConnected = a.type ? connectedByType.get(a.type) : undefined;
                        const bConnected = b.type ? connectedByType.get(b.type) : undefined;
                        if (aConnected && !bConnected) {
                          return -1;
                        }
                        if (!aConnected && bConnected) {
                          return 1;
                        }

                        if (a.comingSoon && !b.comingSoon) {
                          return 1;
                        }
                        if (!a.comingSoon && b.comingSoon) {
                          return -1;
                        }
                        const categoryCompare = a.category.localeCompare(b.category);
                        if (categoryCompare !== 0) {
                          return categoryCompare;
                        }
                        return a.name.localeCompare(b.name);
                      })
                      .map((integration) => (
                        <div
                          key={integration.name}
                          className={clsx(
                            'px-4 py-5 sm:px-6 bg-white dark:bg-neutral-800',
                            integration.comingSoon &&
                              'bg-neutral-200 dark:bg-neutral-700 opacity-80'
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <img
                                src={integration.icon}
                                alt={integration.name}
                                className="h-8 rounded-md"
                              />
                              <h4 className="font-medium m-0 p-0 text-neutral-900 dark:text-white">
                                {integration.name}
                              </h4>
                            </div>
                            {integration.type && connectedByType.has(integration.type) && (
                              <Badge color="green">Connected</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-4">
                            <Badge color="neutral">{integration.category}</Badge>
                            {integration.tags.map((tag) => (
                              <Badge key={tag} color="neutral">
                                {tag}
                              </Badge>
                            ))}
                            {integration.type &&
                              integration.type === emailLoggingIntegration?.type && (
                                <Badge color="sky">Logs emails</Badge>
                              )}
                          </div>
                          <div className={clsx('mt-4', integration.comingSoon && 'opacity-70')}>
                            <IntegrationConnectAction
                              integration={integration}
                              connected={
                                integration.type ? connectedByType.get(integration.type) : undefined
                              }
                              canConnectIntegrations={canConnectIntegrations}
                              onUpgradeClick={() => setShowProUpgradeDialog(true)}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            );
          }}
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet)}</AnimatePresence>
      <ProUpgradeDialog
        open={showProUpgradeDialog}
        onClose={() => setShowProUpgradeDialog(false)}
        title="Unlock AMS & CRM Integrations with Pro"
        description="Connect your agency management and CRM systems so Cara can retrieve, index, and populate data automatically. Upgrade to the Pro plan for unlimited AMS and CRM integrations — and everything else Pro unlocks."
        useCases={INTEGRATIONS_PRO_UPGRADE_USE_CASES}
      />
    </>
  );
};

const IntegrationsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading settings...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

type QQCatalystOutcome = { tone: 'success' | 'error'; title: string; body: string };

/**
 * The query parameters the OAuth callback lands on. They are a contract with
 * pkg/service/api/integration/qqcatalyst_oauth.go, which redirects here with the outcome.
 */
const qqcatalystOutcomeParams = [
  'qqcatalyst_status',
  'qqcatalyst_error',
  'qqcatalyst_message',
  'qqcatalyst_integration_id'
];

/**
 * The server's own message wins wherever it supplies one: it knows why the
 * provider refused.
 */
export const describeQQCatalystOutcome = (params: URLSearchParams): QQCatalystOutcome | null => {
  const message = params.get('qqcatalyst_message');

  switch (params.get('qqcatalyst_status')) {
    case 'connected':
      return {
        tone: 'success',
        title: 'QQCatalyst connected',
        body: message ?? 'Choose the office Cara should use for new prospects, notes, and tasks.'
      };
    case 'error':
      return {
        tone: 'error',
        title: 'QQCatalyst not connected',
        body: message ?? 'We could not connect QQCatalyst. Try again.'
      };
    default:
      return null;
  }
};

const qqcatalystOutcomeToneStyles: Record<QQCatalystOutcome['tone'], string> = {
  success:
    'border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 text-green-900 dark:text-green-200',
  error:
    'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 text-red-900 dark:text-red-200'
};

export const QQCatalystOutcomeBanner = ({ className }: { className?: string }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const outcome = describeQQCatalystOutcome(searchParams);

  if (!outcome) {
    return null;
  }

  const dismiss = () => {
    const remaining = new URLSearchParams(searchParams);
    qqcatalystOutcomeParams.forEach((param) => remaining.delete(param));
    setSearchParams(remaining, { replace: true });
  };

  return (
    <div
      role="status"
      className={clsx(
        'flex items-start gap-3 rounded-lg border p-4',
        qqcatalystOutcomeToneStyles[outcome.tone],
        className
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{outcome.title}</p>
        <p className="mt-1 text-sm">{outcome.body}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10"
      >
        <XMarkIcon className="size-4" />
      </button>
    </div>
  );
};

export const IntegrationStatusBanner = (props: { canConnectIntegrations?: boolean }) => {
  const canConnect = props.canConnectIntegrations ?? true;

  return (
    <div className="overflow-hidden rounded-lg bg-gradient-to-r from-sky-700 to-sky-600">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
          {canConnect ? (
            'Get started with integrations'
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2">
              AMS & CRM integrations
              <ProLabel variant="emphasis" />
            </span>
          )}
        </h3>
        <div
          className={clsx(
            'mt-2',
            !canConnect &&
              'flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4'
          )}
        >
          <p className="m-0 text-sm text-neutral-200">
            {canConnect
              ? "Integrations enable Cara to automatically retrieve, index, and populate data from your existing systems, allowing her to work effectively in your agency's context. Cara can also be configured to write certain information back to your systems."
              : 'Connect your agency management and CRM systems so Cara can retrieve, index, and use your data in workflows. Upgrade to Pro for unlimited AMS and CRM integrations.'}
          </p>
          {!canConnect && (
            <Button
              color="white"
              href="https://docs.getcara.ai/integrations/ezlynx"
              target="_blank"
              rel="noreferrer"
              className="shrink-0 self-start"
            >
              View documentation
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const IntegrationConnectPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/integrations');
    } else {
      navigate(-1);
    }
  };

  return (
    <MountableSlideout onClose={onClose} title="Connect Integration">
      {type === IntegrationType.AmsHawksoft && <ConnectHawksoft />}
      {type === IntegrationType.AmsNowcerts && <ConnectNowCerts />}
      {type === IntegrationType.AmsEzlynx && <ConnectEzLynx />}
      {type === IntegrationType.AmsAppliedEpic && <ConnectAppliedEpic />}
      {type === IntegrationType.Ams_360 && <ConnectAMS360 />}
      {type === IntegrationType.CrmAgencyZoom && <ConnectAgencyZoom />}
      {type === IntegrationType.CrmHubspot && <ConnectHubSpot />}
      {type === IntegrationType.Zapier && <ConnectZapier />}
      {type === IntegrationType.AmsImageright && <ConnectImageRight />}
      {type === IntegrationType.AmsQqcatalyst && <ConnectQQCatalyst />}
    </MountableSlideout>
  );
};

const ValidationErrorsContext = React.createContext<ValidationError[]>([]);

const IntegrationField = (props: {
  label: string;
  description: string;
  placeholder: string;
  type?: string;
  validationPath: string[];
  onChange: (value: string) => void;
}) => {
  const validationErrors = React.useContext(ValidationErrorsContext);
  const error = getValidationError(validationErrors, props.validationPath);
  return (
    <Field>
      <Label>{props.label}</Label>
      <Description>{props.description}</Description>
      <Input
        placeholder={props.placeholder}
        type={props.type}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        invalid={!!error}
      />
      {!!error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  );
};

const credentialErrorMessage = (msg: string) =>
  msg.includes('authentication failed') || msg.includes('invalid_grant')
    ? 'Invalid credentials'
    : msg;

const ConnectIntegrationForm = (props: {
  title: string;
  description: string;
  initialForm: CreateIntegrationInput;
  formatError?: (msg: string) => string;
  submitDisabled?: boolean;
  children: (renderProps: {
    form: CreateIntegrationInput;
    setForm: React.Dispatch<React.SetStateAction<CreateIntegrationInput>>;
    updateForm: (
      fieldParts: string[],
      update: (prev: CreateIntegrationInput) => CreateIntegrationInput
    ) => void;
    setValidationErrors: React.Dispatch<React.SetStateAction<ValidationError[]>>;
  }) => React.ReactNode;
}) => {
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateIntegrationMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateIntegrationInput>(props.initialForm);
  const { updateForm } = useFormActions(setForm, setValidationErrors);
  // Reachable by URL, so the connect form checks rather than trusting the
  // catalog button that normally opens it.
  const canConnect = usePermissions().hasAllPermissions(CREATE_INTEGRATION);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (props.submitDisabled) {
      return;
    }
    submit(form, {
      method: 'POST',
      encType: 'application/json'
    });
  };

  const appError = action?.applicationError?.message;
  const displayError = appError
    ? props.formatError
      ? props.formatError(appError)
      : appError
    : undefined;

  return (
    <ValidationErrorsContext.Provider value={validationErrors}>
      <div>
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          {props.title}
        </h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{props.description}</p>
        {!canConnect && (
          <PermissionNotice className="mt-4">
            You don&rsquo;t have permission to connect integrations.
          </PermissionNotice>
        )}
        <Form className="mt-4" onSubmit={onSubmit}>
          <Fieldset disabled={!canConnect}>
            <FieldGroup>
              {props.children({ form, setForm, updateForm, setValidationErrors })}
            </FieldGroup>
          </Fieldset>
          <div className="mt-6">
            {displayError && <ErrorMessage>{displayError}</ErrorMessage>}
            {canConnect && (
              <PrimaryButton
                className="w-full mt-2"
                type="submit"
                loading={navigation.state === 'submitting'}
                disabled={props.submitDisabled}
              >
                Connect
              </PrimaryButton>
            )}
          </div>
        </Form>
      </div>
    </ValidationErrorsContext.Provider>
  );
};

const ConnectHawksoft = () => (
  <ConnectIntegrationForm
    title="Hawksoft Integration"
    description="Connect your Hawksoft 6 AMS to Cara to automatically retrieve, index, and search data from your agency management system and write activity back."
    initialForm={{ type: IntegrationType.AmsHawksoft, hawksoft: { agencyId: '' } }}
  >
    {({ updateForm }) => (
      <IntegrationField
        label="Agency ID"
        description="Enter your Hawksoft Agency ID."
        placeholder="Agency ID"
        validationPath={['hawksoft', 'agencyId']}
        onChange={(agencyId) =>
          updateForm(['hawksoft', 'agencyId'], (prev) => ({
            ...prev,
            hawksoft: prev.hawksoft ? { ...prev.hawksoft, agencyId } : undefined
          }))
        }
      />
    )}
  </ConnectIntegrationForm>
);

const ConnectNowCerts = () => (
  <ConnectIntegrationForm
    title="NowCerts Integration"
    description="Connect your NowCerts AMS to Cara to automatically retrieve, index, and search data from your agency management system and write activity back."
    initialForm={{
      type: IntegrationType.AmsNowcerts,
      nowcerts: { username: '', password: '', enableSyncTasks: false, enableSyncCalls: false }
    }}
    formatError={credentialErrorMessage}
  >
    {({ updateForm }) => (
      <>
        <IntegrationField
          label="Username"
          description="Enter your NowCerts username."
          placeholder="Username"
          validationPath={['nowcerts', 'username']}
          onChange={(username) =>
            updateForm(['nowcerts', 'username'], (prev) => ({
              ...prev,
              nowcerts: prev.nowcerts ? { ...prev.nowcerts, username } : undefined
            }))
          }
        />
        <IntegrationField
          label="Password"
          description="Enter your NowCerts password."
          placeholder="Password"
          type="password"
          validationPath={['nowcerts', 'password']}
          onChange={(password) =>
            updateForm(['nowcerts', 'password'], (prev) => ({
              ...prev,
              nowcerts: prev.nowcerts ? { ...prev.nowcerts, password } : undefined
            }))
          }
        />
      </>
    )}
  </ConnectIntegrationForm>
);

const ConnectEzLynx = () => {
  const [mode, setMode] = React.useState<'api' | 'csv'>('csv');
  const [form, setForm] = React.useState<CreateIntegrationInput>({
    type: IntegrationType.AmsEzlynx,
    ezlynx: {
      clientId: '',
      clientSecret: '',
      username: '',
      password: '',
      appSecret: '',
      integrationGroupId: '',
      csvImportOnly: true
    }
  });

  return (
    <ConnectIntegrationForm
      title="EZLynx Integration"
      description="Connect your EZLynx AMS to Cara to automatically retrieve, index, and search data from your agency management system."
      initialForm={form}
      formatError={credentialErrorMessage}
    >
      {({ updateForm, setForm: setFormState }) => {
        const handleModeChange = (newMode: 'api' | 'csv') => {
          setMode(newMode);
          const newForm: CreateIntegrationInput = {
            type: IntegrationType.AmsEzlynx,
            ezlynx: {
              clientId: '',
              clientSecret: '',
              username: '',
              password: '',
              appSecret: '',
              integrationGroupId: '',
              csvImportOnly: newMode === 'csv'
            }
          };
          setForm(newForm);
          setFormState(newForm);
        };

        return (
          <>
            <Field>
              <Label>Integration Mode</Label>
              <Description>
                Choose how you want to connect EZLynx to Cara. You can start with CSV import and
                upgrade to API mode later.
              </Description>
              <div className="mt-2 space-y-2">
                <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-white/5">
                  <input
                    type="radio"
                    name="ezlynx-mode"
                    value="csv"
                    checked={mode === 'csv'}
                    onChange={() => handleModeChange('csv')}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-neutral-900 dark:text-white">
                      CSV Import Only
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      Cara will ingest CSV reports via email. No API credentials required.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-white/5">
                  <input
                    type="radio"
                    name="ezlynx-mode"
                    value="api"
                    checked={mode === 'api'}
                    onChange={() => handleModeChange('api')}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-neutral-900 dark:text-white">
                      API Integration
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      Full API access with real-time data sync. Requires API credentials.
                    </div>
                  </div>
                </label>
              </div>
            </Field>

            {mode === 'csv' && (
              <div className="mt-4 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 p-4">
                <div className="flex items-start gap-3">
                  <EnvelopeIcon className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="my-0 text-sm font-medium text-sky-900 dark:text-sky-200">
                      CSV Import Setup
                    </h4>
                    <p className="mt-1 text-sm text-sky-700 dark:text-sky-300">
                      To enable CSV import, forward your EZLynx CSV reports to{' '}
                      <code className="bg-sky-100 dark:bg-sky-500/20 px-1 py-0.5 rounded text-xs font-mono">
                        ezlynx@inbox.getcara.ai
                      </code>
                      . Cara will automatically process CSV attachments and sync the data.
                    </p>
                    <p className="mt-2 text-xs text-sky-600 dark:text-sky-400">
                      <a
                        href="https://docs.getcara.ai/integrations/ezlynx"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-sky-800 dark:hover:text-sky-300"
                      >
                        Learn more about setting up email forwarding →
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {mode === 'api' && (
              <>
                <div className="mt-4">
                  <IntegrationField
                    label="Client ID"
                    description="Enter your EZLynx client ID."
                    placeholder="Client ID"
                    validationPath={['ezlynx', 'clientId']}
                    onChange={(clientId) =>
                      updateForm(['ezlynx', 'clientId'], (prev) => ({
                        ...prev,
                        ezlynx: prev.ezlynx
                          ? { ...prev.ezlynx, clientId, csvImportOnly: false }
                          : undefined
                      }))
                    }
                  />
                  <IntegrationField
                    label="Client Secret"
                    description="Enter your EZLynx client secret."
                    placeholder="Client Secret"
                    type="password"
                    validationPath={['ezlynx', 'clientSecret']}
                    onChange={(clientSecret) =>
                      updateForm(['ezlynx', 'clientSecret'], (prev) => ({
                        ...prev,
                        ezlynx: prev.ezlynx ? { ...prev.ezlynx, clientSecret } : undefined
                      }))
                    }
                  />
                  <IntegrationField
                    label="Username"
                    description="Enter your EZLynx username."
                    placeholder="Username"
                    validationPath={['ezlynx', 'username']}
                    onChange={(username) =>
                      updateForm(['ezlynx', 'username'], (prev) => ({
                        ...prev,
                        ezlynx: prev.ezlynx ? { ...prev.ezlynx, username } : undefined
                      }))
                    }
                  />
                  <IntegrationField
                    label="Password"
                    description="Enter your EZLynx password."
                    placeholder="Password"
                    type="password"
                    validationPath={['ezlynx', 'password']}
                    onChange={(password) =>
                      updateForm(['ezlynx', 'password'], (prev) => ({
                        ...prev,
                        ezlynx: prev.ezlynx ? { ...prev.ezlynx, password } : undefined
                      }))
                    }
                  />
                  <IntegrationField
                    label="App Secret"
                    description="Enter your EZLynx app secret."
                    placeholder="App Secret"
                    type="password"
                    validationPath={['ezlynx', 'appSecret']}
                    onChange={(appSecret) =>
                      updateForm(['ezlynx', 'appSecret'], (prev) => ({
                        ...prev,
                        ezlynx: prev.ezlynx ? { ...prev.ezlynx, appSecret } : undefined
                      }))
                    }
                  />
                  <IntegrationField
                    label="Integration Group ID"
                    description="Enter your EZLynx integration group ID."
                    placeholder="Integration Group ID"
                    validationPath={['ezlynx', 'integrationGroupId']}
                    onChange={(integrationGroupId) =>
                      updateForm(['ezlynx', 'integrationGroupId'], (prev) => ({
                        ...prev,
                        ezlynx: prev.ezlynx ? { ...prev.ezlynx, integrationGroupId } : undefined
                      }))
                    }
                  />
                </div>
                <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 p-4">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    <strong>Note:</strong> With API credentials, Cara can also process CSV emails
                    and use the API to fetch detailed data for each record.
                  </p>
                </div>
              </>
            )}
          </>
        );
      }}
    </ConnectIntegrationForm>
  );
};

const ConnectAppliedEpic = () => (
  <ConnectIntegrationForm
    title="Applied Epic Integration"
    description="Connect your Applied Epic AMS to Cara to automatically retrieve, index, and search data from your agency management system and write activity back."
    initialForm={{
      type: IntegrationType.AmsAppliedEpic,
      appliedEpic: {
        type: AppliedEpicIntegrationType.Api,
        apiCredentials: { key: '', secret: '' }
      }
    }}
  >
    {({ updateForm, setForm, setValidationErrors }) => {
      const [integrationType, setIntegrationType] = React.useState<AppliedEpicIntegrationType>(
        AppliedEpicIntegrationType.Api
      );

      const handleIntegrationTypeChange = (type: AppliedEpicIntegrationType | '') => {
        if (!type) return;
        setIntegrationType(type);
        setForm({
          type: IntegrationType.AmsAppliedEpic,
          appliedEpic: {
            type: type,
            ...(type === AppliedEpicIntegrationType.Api
              ? { apiCredentials: { key: '', secret: '' } }
              : { sdkCredentials: { baseUrl: '', databaseName: '', authKey: '' } })
          }
        });
        setValidationErrors([]);
      };

      return (
        <>
          <Field>
            <Label>Integration Type</Label>
            <Description>Select the type of Applied Epic integration.</Description>
            <Select
              value={integrationType}
              onChange={(e) =>
                handleIntegrationTypeChange(e.target.value as AppliedEpicIntegrationType)
              }
            >
              <option value={AppliedEpicIntegrationType.Api}>API</option>
              <option value={AppliedEpicIntegrationType.Sdk}>SDK</option>
            </Select>
          </Field>

          {integrationType === AppliedEpicIntegrationType.Api && (
            <>
              <IntegrationField
                label="API Key"
                description="Enter your Applied Epic API key."
                placeholder="API Key"
                validationPath={['appliedEpic', 'apiCredentials', 'key']}
                onChange={(key) =>
                  updateForm(['appliedEpic', 'apiCredentials', 'key'], (prev) => ({
                    ...prev,
                    appliedEpic: prev.appliedEpic
                      ? {
                          ...prev.appliedEpic,
                          apiCredentials: prev.appliedEpic.apiCredentials
                            ? { ...prev.appliedEpic.apiCredentials, key }
                            : undefined
                        }
                      : undefined
                  }))
                }
              />
              <IntegrationField
                label="API Secret"
                description="Enter your Applied Epic API secret."
                placeholder="API Secret"
                type="password"
                validationPath={['appliedEpic', 'apiCredentials', 'secret']}
                onChange={(secret) =>
                  updateForm(['appliedEpic', 'apiCredentials', 'secret'], (prev) => ({
                    ...prev,
                    appliedEpic: prev.appliedEpic
                      ? {
                          ...prev.appliedEpic,
                          apiCredentials: prev.appliedEpic.apiCredentials
                            ? { ...prev.appliedEpic.apiCredentials, secret }
                            : undefined
                        }
                      : undefined
                  }))
                }
              />
            </>
          )}

          {integrationType === AppliedEpicIntegrationType.Sdk && (
            <>
              <IntegrationField
                label="Base URL"
                description="Enter your Applied Epic SDK base URL."
                placeholder="https://your-instance.appliedepic.com"
                validationPath={['appliedEpic', 'sdkCredentials', 'baseUrl']}
                onChange={(baseUrl) =>
                  updateForm(['appliedEpic', 'sdkCredentials', 'baseUrl'], (prev) => ({
                    ...prev,
                    appliedEpic: prev.appliedEpic
                      ? {
                          ...prev.appliedEpic,
                          sdkCredentials: prev.appliedEpic.sdkCredentials
                            ? { ...prev.appliedEpic.sdkCredentials, baseUrl }
                            : undefined
                        }
                      : undefined
                  }))
                }
              />
              <IntegrationField
                label="Database Name"
                description="Enter your Applied Epic database name."
                placeholder="Database Name"
                validationPath={['appliedEpic', 'sdkCredentials', 'databaseName']}
                onChange={(databaseName) =>
                  updateForm(['appliedEpic', 'sdkCredentials', 'databaseName'], (prev) => ({
                    ...prev,
                    appliedEpic: prev.appliedEpic
                      ? {
                          ...prev.appliedEpic,
                          sdkCredentials: prev.appliedEpic.sdkCredentials
                            ? { ...prev.appliedEpic.sdkCredentials, databaseName }
                            : undefined
                        }
                      : undefined
                  }))
                }
              />
              <IntegrationField
                label="Auth Key"
                description="Enter your Applied Epic SDK auth key."
                placeholder="Auth Key"
                type="password"
                validationPath={['appliedEpic', 'sdkCredentials', 'authKey']}
                onChange={(authKey) =>
                  updateForm(['appliedEpic', 'sdkCredentials', 'authKey'], (prev) => ({
                    ...prev,
                    appliedEpic: prev.appliedEpic
                      ? {
                          ...prev.appliedEpic,
                          sdkCredentials: prev.appliedEpic.sdkCredentials
                            ? { ...prev.appliedEpic.sdkCredentials, authKey }
                            : undefined
                        }
                      : undefined
                  }))
                }
              />
            </>
          )}
        </>
      );
    }}
  </ConnectIntegrationForm>
);

const ConnectAgencyZoom = () => (
  <ConnectIntegrationForm
    title="AgencyZoom Integration"
    description="Connect your AgencyZoom AMS to Cara to automatically retrieve, index, and search data from your agency management system and write activity back."
    initialForm={{
      type: IntegrationType.CrmAgencyZoom,
      agencyZoom: { username: '', password: '', isSSOLogin: false }
    }}
    formatError={credentialErrorMessage}
  >
    {({ form, updateForm }) => (
      <>
        <IntegrationField
          label="Username"
          description="Enter your AgencyZoom username."
          placeholder="Username"
          validationPath={['agencyzoom', 'username']}
          onChange={(username) =>
            updateForm(['agencyZoom', 'username'], (prev) => ({
              ...prev,
              agencyZoom: prev.agencyZoom ? { ...prev.agencyZoom, username } : undefined
            }))
          }
        />
        <IntegrationField
          label="Password"
          description="Enter your AgencyZoom password."
          placeholder="Password"
          type="password"
          validationPath={['agencyzoom', 'password']}
          onChange={(password) =>
            updateForm(['agencyZoom', 'password'], (prev) => ({
              ...prev,
              agencyZoom: prev.agencyZoom ? { ...prev.agencyZoom, password } : undefined
            }))
          }
        />
        <SwitchField>
          <Label>Use Vertafore SSO</Label>
          <Description>Enable if you use Vertafore SSO to sign into AgencyZoom.</Description>
          <Switch
            checked={!!form.agencyZoom?.isSSOLogin}
            onChange={(enabled) =>
              updateForm(['agencyZoom', 'isSSOLogin'], (prev) => ({
                ...prev,
                agencyZoom: prev.agencyZoom
                  ? { ...prev.agencyZoom, isSSOLogin: enabled }
                  : { username: '', password: '', isSSOLogin: enabled }
              }))
            }
            color="sky"
          />
        </SwitchField>
      </>
    )}
  </ConnectIntegrationForm>
);

const ConnectAMS360 = () => (
  <ConnectIntegrationForm
    title="AMS360 Integration"
    description="Connect your AMS360 AMS to Cara to automatically retrieve, index, and search data from your agency management system."
    initialForm={{
      type: IntegrationType.Ams_360,
      ams360: { appKey: '', agencyNo: '', appAccessToAgencyKey: '' }
    }}
    formatError={(msg) =>
      msg.includes('authentication failed') || msg.includes('invalid')
        ? 'Invalid credentials. Please check your App Key, Agency Number, and App Access to Agency Key and try again.'
        : msg
    }
  >
    {({ updateForm }) => (
      <>
        <div className="rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 p-4">
          <p className="text-sm text-sky-800 dark:text-sky-300">
            <strong>Can't find Cara in the AMS360 integration catalog?</strong> Contact your
            Vertafore account manager or the Cara support team and provide your agency number. We'll
            work together to enable the Cara integration in your AMS360 account.
          </p>
        </div>
        <IntegrationField
          label="App Key"
          description="Enter your AMS360 App Key for authentication."
          placeholder="App Key"
          type="password"
          validationPath={['ams360', 'appKey']}
          onChange={(appKey) =>
            updateForm(['ams360', 'appKey'], (prev) => ({
              ...prev,
              ams360: prev.ams360
                ? { ...prev.ams360, appKey }
                : { appKey, agencyNo: '', appAccessToAgencyKey: '' }
            }))
          }
        />
        <IntegrationField
          label="Agency Number"
          description="Enter your AMS360 Agency Number."
          placeholder="Agency Number"
          validationPath={['ams360', 'agencyNo']}
          onChange={(agencyNo) =>
            updateForm(['ams360', 'agencyNo'], (prev) => ({
              ...prev,
              ams360: prev.ams360
                ? { ...prev.ams360, agencyNo }
                : { appKey: '', agencyNo, appAccessToAgencyKey: '' }
            }))
          }
        />
        <IntegrationField
          label="App Access to Agency Key"
          description="Enter your AMS360 App Access to Agency Key for authentication."
          placeholder="App Access to Agency Key"
          type="password"
          validationPath={['ams360', 'appAccessToAgencyKey']}
          onChange={(appAccessToAgencyKey) =>
            updateForm(['ams360', 'appAccessToAgencyKey'], (prev) => ({
              ...prev,
              ams360: prev.ams360
                ? { ...prev.ams360, appAccessToAgencyKey }
                : { appKey: '', agencyNo: '', appAccessToAgencyKey }
            }))
          }
        />
      </>
    )}
  </ConnectIntegrationForm>
);

const ConnectHubSpot = () => (
  <ConnectIntegrationForm
    title="HubSpot Integration"
    description="Connect your HubSpot CRM to Cara to automatically sync tasks, calls, and other activities."
    initialForm={{ type: IntegrationType.CrmHubspot, hubspot: { accessToken: '' } }}
    formatError={(msg) =>
      msg.includes('authentication failed') || msg.includes('invalid_grant')
        ? 'Invalid access token'
        : msg
    }
  >
    {({ updateForm }) => (
      <IntegrationField
        label="Personal Access Token"
        description="Enter your HubSpot Personal Access Token. You can create one in your HubSpot developer settings."
        placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        type="password"
        validationPath={['hubspot', 'accessToken']}
        onChange={(accessToken) =>
          updateForm(['hubspot', 'accessToken'], (prev) => ({
            ...prev,
            hubspot: { accessToken }
          }))
        }
      />
    )}
  </ConnectIntegrationForm>
);

const ConnectZapier = () => {
  const navigation = useNavigation();
  const submit = useSubmit();

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(
      {
        type: IntegrationType.Zapier
      },
      {
        method: 'POST',
        encType: 'application/json'
      }
    );
  };

  return (
    <div>
      <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
        Zapier Integration
      </h2>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect Zapier to automate workflows between Cara and your favorite apps. Cara will
        automatically generate an API key for this integration.
      </p>
      <Form className="mt-4" onSubmit={onSubmit}>
        <div className="mt-6">
          <PrimaryButton
            className="w-full mt-2"
            type="submit"
            loading={navigation.state === 'submitting'}
          >
            Connect
          </PrimaryButton>
        </div>
      </Form>
    </div>
  );
};

const ConnectImageRight = () => {
  // ImageRight requires an active AMS360 integration to function, so we gate
  // credential submission on AMS360 being connected.
  const { data, loading } = useGetIntegrationsQuery();
  const hasAMS360 = (data?.integrations ?? []).some(
    (integration) => integration.type === IntegrationType.Ams_360
  );

  return (
    <ConnectIntegrationForm
      title="ImageRight Integration"
      description="Connect your ImageRight document management system to Cara. Once you submit your credentials, our support team will reach out to complete setup."
      initialForm={{
        type: IntegrationType.AmsImageright,
        imageRight: { serverUrl: '', username: '', password: '' }
      }}
      formatError={credentialErrorMessage}
      submitDisabled={loading || !hasAMS360}
    >
      {({ updateForm }) => (
        <>
          <div
            className={clsx(
              'rounded-lg border p-4',
              hasAMS360
                ? 'border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10'
                : 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10'
            )}
          >
            <p
              className={clsx(
                'text-sm',
                hasAMS360 ? 'text-sky-800 dark:text-sky-300' : 'text-amber-800 dark:text-amber-300'
              )}
            >
              <strong>ImageRight requires an active AMS360 integration to function.</strong>{' '}
              {hasAMS360
                ? 'Your AMS360 integration is connected, so you can submit your ImageRight credentials below.'
                : 'Connect AMS360 first, then return here to submit your ImageRight credentials.'}
            </p>
          </div>
          <IntegrationField
            label="Server URL"
            description="Enter your ImageRight server URL. ImageRight is self-hosted, so this is the address of your agency's own ImageRight server."
            placeholder="https://imageright.your-agency.com"
            validationPath={['imageRight', 'serverUrl']}
            onChange={(serverUrl) =>
              updateForm(['imageRight', 'serverUrl'], (prev) => ({
                ...prev,
                imageRight: prev.imageRight ? { ...prev.imageRight, serverUrl } : undefined
              }))
            }
          />
          <IntegrationField
            label="Username"
            description="Enter your ImageRight username."
            placeholder="Username"
            validationPath={['imageRight', 'username']}
            onChange={(username) =>
              updateForm(['imageRight', 'username'], (prev) => ({
                ...prev,
                imageRight: prev.imageRight ? { ...prev.imageRight, username } : undefined
              }))
            }
          />
          <IntegrationField
            label="Password"
            description="Enter your ImageRight password."
            placeholder="Password"
            type="password"
            validationPath={['imageRight', 'password']}
            onChange={(password) =>
              updateForm(['imageRight', 'password'], (prev) => ({
                ...prev,
                imageRight: prev.imageRight ? { ...prev.imageRight, password } : undefined
              }))
            }
          />
        </>
      )}
    </ConnectIntegrationForm>
  );
};

const qqcatalystConnectPath = `/app/settings/integrations/connect/${IntegrationType.AmsQqcatalyst}`;

const qqcatalystConnectUrl = () => {
  const params = new URLSearchParams({ returnPath: qqcatalystConnectPath });
  return `${getApiBaseUrl()}/integrations/qqcatalyst/oauth/start?${params.toString()}`;
};

const ConnectQQCatalyst = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const outcome = describeQQCatalystOutcome(searchParams);
  const integrationId =
    outcome?.tone === 'success' ? searchParams.get('qqcatalyst_integration_id') : null;

  React.useEffect(() => {
    if (!integrationId) {
      return;
    }
    navigate(`/app/settings/integrations/manage/${integrationId}?${searchParams.toString()}`, {
      replace: true
    });
  }, [integrationId, navigate, searchParams]);

  if (integrationId) {
    return (
      <div className="mt-6 flex justify-center">
        <Spinner size="size-6" color="neutral" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
        QQCatalyst Integration
      </h2>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect your QQCatalyst agency management system to Cara so she can look up customers,
        prospects, and policies during calls, and write notes, tasks, and documents back to the
        right record.
      </p>
      {outcome?.tone === 'error' && <QQCatalystOutcomeBanner className="mt-4" />}
      <QQCatalystPreConnect />
    </div>
  );
};

const QQCatalystPreConnect = () => {
  const oauthURL = qqcatalystConnectUrl();
  return (
    <>
      <div className="mt-4 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 p-4">
        <p className="text-sm text-sky-800 dark:text-sky-300">
          <strong>Before you connect:</strong> your agency needs to be a QQCatalyst subscriber. Sign
          in with a QQCatalyst user who has access to every office you want Cara to see — Cara can
          only read the book that user can read. If your agency has not yet approved Cara as an API
          partner, contact your Vertafore account manager with your QQID and we will work with them
          to enable it.
        </p>
      </div>
      <div className="mt-6">
        <Button color="sky" className="w-full" href={oauthURL}>
          Connect with QQCatalyst
        </Button>
      </div>
    </>
  );
};

export const IntegrationManagePage = () => {
  const loader = useLoaderData() as {
    data: Promise<{
      integration: IntegrationDetailFieldsFragment;
      integrations: IntegrationFieldsFragment[];
    }>;
  };
  const navigate = useNavigate();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_INTEGRATION);

  const onClose = () => {
    navigate('/app/settings/integrations');
  };

  return (
    <MountableSlideout onClose={onClose} title="Manage Integration" size="xl">
      {!canUpdate && (
        <PermissionNotice className="mb-6">
          You can see how this integration is configured but not change it.
        </PermissionNotice>
      )}
      <Fieldset disabled={!canUpdate}>
        <React.Suspense fallback={<IntegrationsManagePageLoader />}>
          <Await resolve={loader?.data}>
            {(data) => {
              const sentEmailLoggingSwitch = data?.integration && (
                <SentEmailLoggingSettings
                  integration={data.integration}
                  emailLoggingIntegration={data.integrations.find(
                    (connected) => connected.logsSentEmails
                  )}
                  canUpdate={canUpdate}
                />
              );
              return data?.integration?.type === IntegrationType.AmsHawksoft ? (
                <ManageHawksoft
                  integration={data.integration}
                  sentEmailLoggingSwitch={sentEmailLoggingSwitch}
                />
              ) : data?.integration?.type === IntegrationType.AmsNowcerts ? (
                <ManageNowCerts
                  integration={data.integration}
                  sentEmailLoggingSwitch={sentEmailLoggingSwitch}
                />
              ) : data?.integration?.type === IntegrationType.AmsEzlynx ? (
                <ManageEzLynx
                  integration={data.integration}
                  sentEmailLoggingSwitch={sentEmailLoggingSwitch}
                />
              ) : data?.integration?.type === IntegrationType.AmsAppliedEpic ? (
                <ManageAppliedEpic
                  integration={data.integration}
                  sentEmailLoggingSwitch={sentEmailLoggingSwitch}
                />
              ) : data?.integration?.type === IntegrationType.Ams_360 ? (
                <ManageAMS360
                  integration={data.integration}
                  sentEmailLoggingSwitch={sentEmailLoggingSwitch}
                />
              ) : data?.integration?.type === IntegrationType.CrmAgencyZoom ? (
                <ManageAgencyZoom integration={data.integration} />
              ) : data?.integration?.type === IntegrationType.CrmHubspot ? (
                <ManageHubSpot integration={data.integration} />
              ) : data?.integration?.type === IntegrationType.Zapier ? (
                <ManageZapier integration={data.integration} />
              ) : data?.integration?.type === IntegrationType.AmsImageright ? (
                <ManageImageRight integration={data.integration} />
              ) : data?.integration?.type === IntegrationType.AmsQqcatalyst ? (
                <ManageQQCatalyst
                  integration={data.integration}
                  sentEmailLoggingSwitch={sentEmailLoggingSwitch}
                />
              ) : null;
            }}
          </Await>
        </React.Suspense>
      </Fieldset>
    </MountableSlideout>
  );
};

const IntegrationsManagePageLoader = () => {
  return (
    <div className="px-6">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading integration...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

type ManageAmsProps = {
  integration: IntegrationDetailFieldsFragment;
  sentEmailLoggingSwitch: React.ReactNode;
};

const ManageHawksoft = ({ integration, sentEmailLoggingSwitch }: ManageAmsProps) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [enableSyncTasks, setEnableSyncTasks] = React.useState(
    integration.configuration?.hawksoft?.enableSyncServiceRequests || false
  );
  const [enableSyncCalls, setEnableSyncCalls] = React.useState(
    integration.configuration?.hawksoft?.enableSyncCallNotes || false
  );
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);

  // Update local state when integration prop changes
  React.useEffect(() => {
    setEnableSyncTasks(integration.configuration?.hawksoft?.enableSyncServiceRequests || false);
    setEnableSyncCalls(integration.configuration?.hawksoft?.enableSyncCallNotes || false);
  }, [
    integration.configuration?.hawksoft?.enableSyncServiceRequests,
    integration.configuration?.hawksoft?.enableSyncCallNotes
  ]);

  // Unified handler for sync toggles
  const handleSyncToggle = async (
    type: 'enableSyncServiceRequests' | 'enableSyncCallNotes',
    enabled: boolean
  ) => {
    if (type === 'enableSyncServiceRequests') setEnableSyncTasks(enabled);
    if (type === 'enableSyncCallNotes') setEnableSyncCalls(enabled);

    if (type === 'enableSyncServiceRequests') setLoadingTasks(true);
    if (type === 'enableSyncCallNotes') setLoadingCalls(true);

    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            hawksoft: {
              [type]: enabled
            }
          }
        }
      });
      if (type === 'enableSyncServiceRequests') {
        setShowSavedTasks(true);
        setTimeout(() => setShowSavedTasks(false), 2000);
      } else if (type === 'enableSyncCallNotes') {
        setShowSavedCalls(true);
        setTimeout(() => setShowSavedCalls(false), 2000);
      }
    } catch {
      if (type === 'enableSyncServiceRequests') setEnableSyncTasks(!enabled);
      if (type === 'enableSyncCallNotes') setEnableSyncCalls(!enabled);
    } finally {
      if (type === 'enableSyncServiceRequests') setLoadingTasks(false);
      if (type === 'enableSyncCallNotes') setLoadingCalls(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          Hawksoft Integration
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect your Hawksoft 6 AMS to Cara to automatically retrieve, index, and search data from
        your agency management system and write activity back.
      </p>
      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Agency ID</dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {integration.configuration?.hawksoft?.agencyId}
            </dd>
          </div>
        </div>
      </dl>
      <div className="mt-4 space-y-4">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Sync Settings</h3>
        <SwitchField>
          <div className="flex items-center gap-2" data-slot="label">
            <Label>Enable Sync Tasks</Label>
            {showSavedTasks && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync service requests from Cara to Hawksoft.</Description>
          <Switch
            checked={enableSyncTasks}
            onChange={(enabled) => handleSyncToggle('enableSyncServiceRequests', enabled)}
            disabled={loadingTasks}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Calls</Label>
            {showSavedCalls && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync call notes from Cara to Hawksoft.</Description>
          <Switch
            checked={enableSyncCalls}
            onChange={(enabled) => handleSyncToggle('enableSyncCallNotes', enabled)}
            disabled={loadingCalls}
            color="sky"
          />
        </SwitchField>
        {sentEmailLoggingSwitch}
      </div>

      <div className="mt-4">
        <IntegrationSyncLogs integration={integration} />
      </div>
    </div>
  );
};

const ManageNowCerts = ({ integration, sentEmailLoggingSwitch }: ManageAmsProps) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [enableSyncAccounts, setEnableSyncAccounts] = React.useState(
    integration.configuration?.nowcerts?.enableSyncAccounts || false
  );
  const [enableSyncTasks, setEnableSyncTasks] = React.useState(
    integration.configuration?.nowcerts?.enableSyncTasks || false
  );
  const [enableSyncCalls, setEnableSyncCalls] = React.useState(
    integration.configuration?.nowcerts?.enableSyncCalls || false
  );
  const [showSavedAccounts, setShowSavedAccounts] = React.useState(false);
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);
  const [prospectReferralSource, setProspectReferralSource] = React.useState<string>(
    integration.configuration?.nowcerts?.prospectReferralSource ?? 'Cara AI'
  );
  const [loadingReferralSource, setLoadingReferralSource] = React.useState(false);
  const [showSavedReferralSource, setShowSavedReferralSource] = React.useState(false);
  const [referralSourceError, setReferralSourceError] = React.useState<string>();

  const [showUpdateCredentials, setShowUpdateCredentials] = React.useState(false);
  const [showUpdateCredentialsSuccess, setShowUpdateCredentialsSuccess] = React.useState(false);
  const [username, setUsername] = React.useState<string>(
    integration.configuration?.nowcerts?.username || ''
  );
  const [password, setPassword] = React.useState<string>();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();

  // Update local state when integration prop changes
  React.useEffect(() => {
    setEnableSyncAccounts(integration.configuration?.nowcerts?.enableSyncAccounts || false);
    setEnableSyncTasks(integration.configuration?.nowcerts?.enableSyncTasks || false);
    setEnableSyncCalls(integration.configuration?.nowcerts?.enableSyncCalls || false);
    setProspectReferralSource(
      integration.configuration?.nowcerts?.prospectReferralSource ?? 'Cara AI'
    );
  }, [
    integration.configuration?.nowcerts?.enableSyncAccounts,
    integration.configuration?.nowcerts?.enableSyncTasks,
    integration.configuration?.nowcerts?.enableSyncCalls,
    integration.configuration?.nowcerts?.prospectReferralSource
  ]);

  // Unified handler for sync toggles
  const handleSyncToggle = async (
    type: 'enableSyncAccounts' | 'enableSyncTasks' | 'enableSyncCalls',
    enabled: boolean
  ) => {
    if (type === 'enableSyncAccounts') setEnableSyncAccounts(enabled);
    if (type === 'enableSyncTasks') setEnableSyncTasks(enabled);
    if (type === 'enableSyncCalls') setEnableSyncCalls(enabled);

    if (type === 'enableSyncAccounts') setLoadingAccounts(true);
    if (type === 'enableSyncTasks') setLoadingTasks(true);
    if (type === 'enableSyncCalls') setLoadingCalls(true);

    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            nowcerts: {
              [type]: enabled
            }
          }
        }
      });
      if (type === 'enableSyncAccounts') {
        setShowSavedAccounts(true);
        setTimeout(() => setShowSavedAccounts(false), 2000);
      } else if (type === 'enableSyncTasks') {
        setShowSavedTasks(true);
        setTimeout(() => setShowSavedTasks(false), 2000);
      } else if (type === 'enableSyncCalls') {
        setShowSavedCalls(true);
        setTimeout(() => setShowSavedCalls(false), 2000);
      }
    } catch {
      if (type === 'enableSyncAccounts') setEnableSyncAccounts(!enabled);
      if (type === 'enableSyncTasks') setEnableSyncTasks(!enabled);
      if (type === 'enableSyncCalls') setEnableSyncCalls(!enabled);
    } finally {
      if (type === 'enableSyncAccounts') setLoadingAccounts(false);
      if (type === 'enableSyncTasks') setLoadingTasks(false);
      if (type === 'enableSyncCalls') setLoadingCalls(false);
    }
  };

  const handleSaveReferralSource = async () => {
    setLoadingReferralSource(true);
    setReferralSourceError(undefined);
    try {
      const result = await updateIntegration({
        variables: {
          id: integration.id,
          input: { nowcerts: { prospectReferralSource } }
        }
      });
      if (result.errors?.length) {
        setReferralSourceError(result.errors[0].message);
        return;
      }
      setShowSavedReferralSource(true);
      setTimeout(() => setShowSavedReferralSource(false), 2000);
    } catch (e) {
      setReferralSourceError(e instanceof Error ? e.message : 'Failed to save referral source');
    } finally {
      setLoadingReferralSource(false);
    }
  };

  const handleUpdateCredentials = async () => {
    try {
      setLoading(true);
      const result = await updateIntegration({
        variables: {
          id: integration.id,
          input: { nowcerts: { username, password } }
        }
      });
      if (result.errors?.length) {
        setError(result.errors[0].message);
      } else {
        setShowUpdateCredentialsSuccess(true);
        setShowUpdateCredentials(false);
        setTimeout(() => setShowUpdateCredentialsSuccess(false), 2000);
      }
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          NowCerts Integration
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect your NowCerts AMS to Cara to automatically retrieve, index, and search data from
        your agency management system and write activity back.
      </p>
      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white flex items-center gap-2">
              Username
              {showUpdateCredentialsSuccess && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                  <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Updated</p>
                </div>
              )}
            </dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {username || 'Not configured'}
            </dd>
          </div>
          {!showUpdateCredentials && (
            <Headless.Button
              type="button"
              onClick={() => setShowUpdateCredentials(true)}
              className="flex text-sm/6 text-sky-600 dark:text-sky-400 underline hover:text-sky-700 dark:hover:text-sky-300 cursor-pointer"
            >
              Update credentials
            </Headless.Button>
          )}
          {showUpdateCredentials && (
            <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
              <Form onSubmit={handleUpdateCredentials}>
                <Fieldset>
                  <FieldGroup>
                    <div className="flex flex-row gap-4">
                      <Field className="w-full">
                        <Label>Username</Label>
                        <Input
                          placeholder="Username"
                          value={username}
                          onChange={(e) => setUsername(e.currentTarget.value)}
                          className="w-full"
                        />
                      </Field>
                      <Field className="w-full">
                        <Label>Password</Label>
                        <Input
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.currentTarget.value)}
                          type="password"
                          className="w-full"
                        />
                      </Field>
                    </div>
                  </FieldGroup>
                </Fieldset>
                <div className="mt-6">
                  {error && <ErrorMessage className="mb-2">{error}</ErrorMessage>}
                  <Button type="submit" color="sky" loading={loading}>
                    Update credentials
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </div>
      </dl>

      <div className="mt-4 space-y-4">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Sync Settings</h3>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Create accounts in NowCerts</Label>
            {showSavedAccounts && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>
            If enabled, Cara will create accounts for clients that don't exist. If disabled, Cara
            will not write data for clients that don't already exist in NowCerts.
          </Description>
          <Switch
            checked={enableSyncAccounts}
            onChange={(enabled) => handleSyncToggle('enableSyncAccounts', enabled)}
            disabled={loadingAccounts}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Tasks</Label>
            {showSavedTasks && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync tasks from Cara to NowCerts.</Description>
          <Switch
            checked={enableSyncTasks}
            onChange={(enabled) => handleSyncToggle('enableSyncTasks', enabled)}
            disabled={loadingTasks}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Calls</Label>
            {showSavedCalls && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync calls from Cara to NowCerts.</Description>
          <Switch
            checked={enableSyncCalls}
            onChange={(enabled) => handleSyncToggle('enableSyncCalls', enabled)}
            disabled={loadingCalls}
            color="sky"
          />
        </SwitchField>
        {sentEmailLoggingSwitch}
      </div>

      <div className="mt-6 space-y-3">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Prospect Settings</h3>
        <Field>
          <div className="flex items-center gap-2">
            <Label>Referral Source</Label>
            {showSavedReferralSource && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>
            {
              'Shown on new NowCerts prospects Cara creates. Save with a blank value to send no referral source. Unsaved integrations still default to "Cara AI" on the server until you save a value here.'
            }
          </Description>
          <div className="mt-2 flex items-center gap-3">
            <Input
              value={prospectReferralSource}
              onChange={(e) => {
                setReferralSourceError(undefined);
                setProspectReferralSource(e.currentTarget.value);
              }}
              placeholder="e.g. Cara AI"
              className="max-w-xs"
              disabled={loadingReferralSource}
            />
            <Button
              type="button"
              color="sky"
              loading={loadingReferralSource}
              disabled={loadingReferralSource}
              onClick={handleSaveReferralSource}
            >
              Save
            </Button>
          </div>
          {referralSourceError && (
            <ErrorMessage className="mt-2">{referralSourceError}</ErrorMessage>
          )}
        </Field>
      </div>

      <div className="mt-4">
        <IntegrationSyncLogs integration={integration} />
      </div>
    </div>
  );
};

const EzLynxCredentialFields = (props: {
  clientId: string;
  clientSecret: string | undefined;
  username: string;
  password: string | undefined;
  appSecret: string | undefined;
  integrationGroupId: string;
  setClientId: (value: string) => void;
  setClientSecret: (value: string) => void;
  setUsername: (value: string) => void;
  setPassword: (value: string) => void;
  setAppSecret: (value: string) => void;
  setIntegrationGroupId: (value: string) => void;
  placeholders?: {
    clientId?: string;
    clientSecret?: string;
    username?: string;
    password?: string;
    appSecret?: string;
    integrationGroupId?: string;
  };
}) => {
  const placeholders = {
    clientId: 'Client ID',
    clientSecret: 'Client Secret',
    username: 'Username',
    password: 'Password',
    appSecret: 'App Secret',
    integrationGroupId: 'Integration Group ID',
    ...props.placeholders
  };

  return (
    <div className="space-y-4">
      <Field>
        <Label>Client ID</Label>
        <Input
          placeholder={placeholders.clientId}
          value={props.clientId}
          onChange={(e) => props.setClientId(e.currentTarget.value)}
        />
      </Field>
      <Field>
        <Label>Client Secret</Label>
        <Input
          placeholder={placeholders.clientSecret}
          value={props.clientSecret}
          onChange={(e) => props.setClientSecret(e.currentTarget.value)}
          type="password"
        />
      </Field>
      <Field>
        <Label>Username</Label>
        <Input
          placeholder={placeholders.username}
          value={props.username}
          onChange={(e) => props.setUsername(e.currentTarget.value)}
        />
      </Field>
      <Field>
        <Label>Password</Label>
        <Input
          placeholder={placeholders.password}
          value={props.password}
          onChange={(e) => props.setPassword(e.currentTarget.value)}
          type="password"
        />
      </Field>
      <Field>
        <Label>App Secret</Label>
        <Input
          placeholder={placeholders.appSecret}
          value={props.appSecret}
          onChange={(e) => props.setAppSecret(e.currentTarget.value)}
          type="password"
        />
      </Field>
      <Field>
        <Label>Integration Group ID</Label>
        <Input
          placeholder={placeholders.integrationGroupId}
          value={props.integrationGroupId}
          onChange={(e) => props.setIntegrationGroupId(e.currentTarget.value)}
        />
      </Field>
    </div>
  );
};

const ManageEzLynx = ({ integration, sentEmailLoggingSwitch }: ManageAmsProps) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [showUpdateCredentials, setShowUpdateCredentials] = React.useState(false);
  const [showUpdateCredentialsSuccess, setShowUpdateCredentialsSuccess] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();

  const [clientId, setClientId] = React.useState<string>(
    integration.configuration?.ezlynx?.clientId || ''
  );
  const [clientSecret, setClientSecret] = React.useState<string>();
  const [username, setUsername] = React.useState<string>(
    integration.configuration?.ezlynx?.username || ''
  );
  const [password, setPassword] = React.useState<string>();
  const [appSecret, setAppSecret] = React.useState<string>();
  const [integrationGroupId, setIntegrationGroupId] = React.useState<string>(
    integration.configuration?.ezlynx?.integrationGroupId || ''
  );

  const hasCredentials =
    integration.configuration?.ezlynx?.clientId &&
    integration.configuration?.ezlynx?.username &&
    integration.configuration?.ezlynx?.integrationGroupId;
  const isCSVOnly = integration.configuration?.ezlynx?.csvImportOnly || !hasCredentials;

  const handleUpdateCredentials = async () => {
    try {
      setLoading(true);
      setError(undefined);
      const result = await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            ezlynx: {
              clientId: clientId || undefined,
              clientSecret: clientSecret || undefined,
              username: username || undefined,
              password: password || undefined,
              appSecret: appSecret || undefined,
              integrationGroupId: integrationGroupId || undefined,
              csvImportOnly: false // When credentials are provided, disable CSV-only mode
            }
          }
        }
      });
      if (result.errors?.length) {
        setError(result.errors[0].message);
      } else {
        setShowUpdateCredentialsSuccess(true);
        setShowUpdateCredentials(false);
        setTimeout(() => setShowUpdateCredentialsSuccess(false), 2000);
      }
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          EZLynx Integration
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect your EZLynx AMS to Cara to automatically retrieve, index, and search data from your
        agency management system.
      </p>

      {isCSVOnly && (
        <div className="mt-4 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 p-4">
          <div className="flex items-start gap-3">
            <EnvelopeIcon className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="my-0 text-sm font-medium text-sky-900 dark:text-sky-200">
                CSV Import Mode
              </h4>
              <p className="mt-1 text-sm text-sky-700 dark:text-sky-300">
                This integration is configured for CSV import only. Forward your EZLynx CSV reports
                to{' '}
                <code className="bg-sky-100 dark:bg-sky-500/20 px-1 py-0.5 rounded text-xs font-mono">
                  ezlynx@inbox.getcara.ai
                </code>{' '}
                to automatically sync data.
              </p>
              <p className="mt-2 text-xs text-sky-600 dark:text-sky-400">
                <a
                  href="https://docs.getcara.ai/integrations/ezlynx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-sky-800 dark:hover:text-sky-300"
                >
                  Learn more about setting up email forwarding →
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {hasCredentials && (
        <div className="mt-4 rounded-lg border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 p-4">
          <div className="flex items-start gap-3">
            <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-green-900 dark:text-green-200">
                API Integration Active
              </h4>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                This integration has API credentials configured. Cara can sync data via API and also
                process CSV emails with enhanced data fetching.
              </p>
            </div>
          </div>
        </div>
      )}

      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
              Integration Mode
            </dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {isCSVOnly ? 'CSV Import Only' : 'API Integration'}
            </dd>
          </div>
          {hasCredentials && (
            <>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
                  Client ID
                </dt>
                <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
                  {integration.configuration?.ezlynx?.clientId || 'Not configured'}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Username</dt>
                <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
                  {integration.configuration?.ezlynx?.username || 'Not configured'}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
                  Integration Group ID
                </dt>
                <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
                  {integration.configuration?.ezlynx?.integrationGroupId || 'Not configured'}
                </dd>
              </div>
            </>
          )}
        </div>
      </dl>

      {isCSVOnly && (
        <div className="mt-4">
          {!showUpdateCredentials && (
            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 p-4">
              <h4 className="my-0 text-sm font-medium text-neutral-900 dark:text-white mb-2">
                Upgrade to API Integration
              </h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                Add API credentials to enable real-time data sync and enhanced CSV processing with
                detailed data fetching.
              </p>
              <Button
                color="sky"
                onClick={() => setShowUpdateCredentials(true)}
                className="w-full sm:w-auto"
              >
                Add API Credentials
              </Button>
            </div>
          )}

          {showUpdateCredentials && (
            <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
              <h4 className="text-sm font-medium text-neutral-900 dark:text-white mb-4">
                {showUpdateCredentialsSuccess && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <CheckIcon className="size-3 text-green-500 dark:text-green-400" />
                    <p className="text-xs/6 text-green-600 dark:text-green-400">
                      Credentials updated successfully
                    </p>
                  </div>
                )}
                Add API Credentials
              </h4>
              <Form onSubmit={handleUpdateCredentials}>
                <Fieldset>
                  <FieldGroup>
                    <EzLynxCredentialFields
                      clientId={clientId}
                      clientSecret={clientSecret}
                      username={username}
                      password={password}
                      appSecret={appSecret}
                      integrationGroupId={integrationGroupId}
                      setClientId={setClientId}
                      setClientSecret={setClientSecret}
                      setUsername={setUsername}
                      setPassword={setPassword}
                      setAppSecret={setAppSecret}
                      setIntegrationGroupId={setIntegrationGroupId}
                    />
                  </FieldGroup>
                </Fieldset>
                <div className="mt-6">
                  {error && <ErrorMessage className="mb-2">{error}</ErrorMessage>}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      color="white"
                      onClick={() => {
                        setShowUpdateCredentials(false);
                        setError(undefined);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" color="sky" loading={loading}>
                      Update Credentials
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          )}
        </div>
      )}

      {hasCredentials && !showUpdateCredentials && (
        <div className="mt-4">
          <Headless.Button
            type="button"
            onClick={() => setShowUpdateCredentials(true)}
            className="flex text-sm/6 text-sky-600 dark:text-sky-400 underline hover:text-sky-700 dark:hover:text-sky-300 cursor-pointer"
          >
            Update credentials
          </Headless.Button>
        </div>
      )}

      {hasCredentials && showUpdateCredentials && (
        <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <h4 className="text-sm font-medium text-neutral-900 dark:text-white mb-4">
            {showUpdateCredentialsSuccess && (
              <div className="flex items-center gap-1.5 mb-2">
                <CheckIcon className="size-3 text-green-500 dark:text-green-400" />
                <p className="text-xs/6 text-green-600 dark:text-green-400">
                  Credentials updated successfully
                </p>
              </div>
            )}
            Update API Credentials
          </h4>
          <Form onSubmit={handleUpdateCredentials}>
            <Fieldset>
              <FieldGroup>
                <EzLynxCredentialFields
                  clientId={clientId}
                  clientSecret={clientSecret}
                  username={username}
                  password={password}
                  appSecret={appSecret}
                  integrationGroupId={integrationGroupId}
                  setClientId={setClientId}
                  setClientSecret={setClientSecret}
                  setUsername={setUsername}
                  setPassword={setPassword}
                  setAppSecret={setAppSecret}
                  setIntegrationGroupId={setIntegrationGroupId}
                  placeholders={{
                    clientSecret: 'Client Secret (leave blank to keep current)',
                    password: 'Password (leave blank to keep current)',
                    appSecret: 'App Secret (leave blank to keep current)'
                  }}
                />
              </FieldGroup>
            </Fieldset>
            <div className="mt-6">
              {error && <ErrorMessage className="mb-2">{error}</ErrorMessage>}
              <div className="flex gap-2">
                <Button
                  type="button"
                  color="white"
                  onClick={() => {
                    setShowUpdateCredentials(false);
                    setError(undefined);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" color="sky" loading={loading}>
                  Update Credentials
                </Button>
              </div>
            </div>
          </Form>
        </div>
      )}

      <SentEmailLoggingSection>{sentEmailLoggingSwitch}</SentEmailLoggingSection>

      <div className="mt-4">
        <IntegrationSyncLogs integration={integration} />
      </div>
    </div>
  );
};

const ManageAppliedEpic = ({ integration, sentEmailLoggingSwitch }: ManageAmsProps) => {
  const [updateIntegration] = useUpdateIntegrationMutation();

  const integrationType = integration.configuration?.appliedEpic?.type;
  const sdkCredentials = integration.configuration?.appliedEpic?.sdkCredentials;
  const isSdk = integrationType === AppliedEpicIntegrationType.Sdk;

  const [showUpdateCredentials, setShowUpdateCredentials] = React.useState(false);
  const [showUpdateCredentialsSuccess, setShowUpdateCredentialsSuccess] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();

  // Credential form state. Defaults to the integration's current type so users
  // typically only re-enter secrets, but switching API <-> SDK is supported.
  const [formType, setFormType] = React.useState<AppliedEpicIntegrationType>(
    integrationType ?? AppliedEpicIntegrationType.Api
  );
  const [apiKey, setApiKey] = React.useState('');
  const [apiSecret, setApiSecret] = React.useState('');
  const [baseUrl, setBaseUrl] = React.useState(sdkCredentials?.baseUrl ?? '');
  const [databaseName, setDatabaseName] = React.useState(sdkCredentials?.databaseName ?? '');
  const [authKey, setAuthKey] = React.useState('');

  const handleUpdateCredentials = async () => {
    const input: UpdateAppliedEpicIntegrationInput =
      formType === AppliedEpicIntegrationType.Api
        ? { type: formType, apiCredentials: { key: apiKey, secret: apiSecret } }
        : { type: formType, sdkCredentials: { baseUrl, databaseName, authKey } };

    try {
      setLoading(true);
      setError(undefined);
      const result = await updateIntegration({
        variables: {
          id: integration.id,
          input: { appliedEpic: input }
        }
      });
      if (result.errors?.length) {
        setError(result.errors[0].message);
      } else {
        setShowUpdateCredentialsSuccess(true);
        setShowUpdateCredentials(false);
        setTimeout(() => setShowUpdateCredentialsSuccess(false), 2000);
      }
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          Applied Epic Integration
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect your Applied Epic AMS to Cara so it can locate and index client records on demand.
        Applied Epic does not support bulk client enumeration, so records are fetched the first time
        Cara needs them (for example, when an inbound email or call matches a client).
      </p>

      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white flex items-center gap-2">
              Integration Type
              {showUpdateCredentialsSuccess && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                  <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Updated</p>
                </div>
              )}
            </dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {integrationType || 'Not configured'}
            </dd>
          </div>
          {isSdk && sdkCredentials && (
            <>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
                  SDK Base URL
                </dt>
                <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 break-all">
                  {sdkCredentials.baseUrl}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
                  Database Name
                </dt>
                <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
                  {sdkCredentials.databaseName}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Auth Key</dt>
                <dd className="text-sm/6 text-neutral-500 dark:text-neutral-400 italic">
                  stored — not displayed
                </dd>
              </div>
            </>
          )}
          {!isSdk && (
            <div className="flex flex-col gap-0.5">
              <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
                API Credentials
              </dt>
              <dd className="text-sm/6 text-neutral-500 dark:text-neutral-400 italic">
                stored — not displayed
              </dd>
            </div>
          )}

          {!showUpdateCredentials && (
            <Headless.Button
              type="button"
              onClick={() => setShowUpdateCredentials(true)}
              className="flex text-sm/6 text-sky-600 dark:text-sky-400 underline hover:text-sky-700 dark:hover:text-sky-300 cursor-pointer"
            >
              Update credentials
            </Headless.Button>
          )}
          {showUpdateCredentials && (
            <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
              <Form onSubmit={handleUpdateCredentials}>
                <Fieldset>
                  <FieldGroup>
                    <Field>
                      <Label>Integration Type</Label>
                      <Description>Select the type of Applied Epic integration.</Description>
                      <Select
                        value={formType}
                        onChange={(e) => setFormType(e.target.value as AppliedEpicIntegrationType)}
                      >
                        <option value={AppliedEpicIntegrationType.Api}>API</option>
                        <option value={AppliedEpicIntegrationType.Sdk}>SDK</option>
                      </Select>
                    </Field>

                    {formType === AppliedEpicIntegrationType.Api && (
                      <div className="flex flex-row gap-4">
                        <Field className="w-full">
                          <Label>API Key</Label>
                          <Input
                            placeholder="API Key"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.currentTarget.value)}
                            className="w-full"
                          />
                        </Field>
                        <Field className="w-full">
                          <Label>API Secret</Label>
                          <Input
                            placeholder="API Secret"
                            value={apiSecret}
                            onChange={(e) => setApiSecret(e.currentTarget.value)}
                            type="password"
                            className="w-full"
                          />
                        </Field>
                      </div>
                    )}

                    {formType === AppliedEpicIntegrationType.Sdk && (
                      <>
                        <Field>
                          <Label>Base URL</Label>
                          <Input
                            placeholder="https://your-instance.appliedepic.com"
                            value={baseUrl}
                            onChange={(e) => setBaseUrl(e.currentTarget.value)}
                            className="w-full"
                          />
                        </Field>
                        <Field>
                          <Label>Database Name</Label>
                          <Input
                            placeholder="Database Name"
                            value={databaseName}
                            onChange={(e) => setDatabaseName(e.currentTarget.value)}
                            className="w-full"
                          />
                        </Field>
                        <Field>
                          <Label>Auth Key</Label>
                          <Input
                            placeholder="Auth Key"
                            value={authKey}
                            onChange={(e) => setAuthKey(e.currentTarget.value)}
                            type="password"
                            className="w-full"
                          />
                        </Field>
                      </>
                    )}
                  </FieldGroup>
                </Fieldset>
                <div className="mt-6">
                  {error && <ErrorMessage className="mb-2">{error}</ErrorMessage>}
                  <Button type="submit" color="sky" loading={loading}>
                    Update credentials
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </div>
      </dl>

      <SentEmailLoggingSection>{sentEmailLoggingSwitch}</SentEmailLoggingSection>

      <div className="mt-4">
        <IntegrationSyncLogs integration={integration} hideSyncButton />
      </div>
    </div>
  );
};

const PendingSupportBanner = () => (
  <div className="mb-4 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 p-4">
    <p className="text-sm text-sky-800 dark:text-sky-300">
      <strong>Your credentials have been submitted.</strong> Our support team will reach out to
      complete setup.
    </p>
  </div>
);

const ManageCredentialIntegration = (props: {
  integration: IntegrationDetailFieldsFragment;
  title: string;
  description: string;
  username?: string;
  // When provided, a read-only server URL row and a server URL input are shown.
  // ImageRight is self-hosted per-agency, so it collects a server URL alongside credentials.
  serverUrl?: string;
  showServerUrl?: boolean;
  buildUpdateInput: (fields: {
    serverUrl: string;
    username: string;
    password: string;
  }) => UpdateIntegrationInput;
}) => {
  const { integration } = props;
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [showUpdateCredentials, setShowUpdateCredentials] = React.useState(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [serverUrl, setServerUrl] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');

  const handleUpdateCredentials = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setLoading(true);
      setError(undefined);
      const result = await updateIntegration({
        variables: {
          id: integration.id,
          input: props.buildUpdateInput({ serverUrl, username, password })
        }
      });
      if (result.errors?.length) {
        setError(result.errors[0].message);
      } else {
        setShowUpdateSuccess(true);
        setShowUpdateCredentials(false);
        setServerUrl('');
        setUsername('');
        setPassword('');
        setTimeout(() => setShowUpdateSuccess(false), 2000);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          {props.title}
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{props.description}</p>

      <div className="mt-4">
        {integration.state === IntegrationState.Pending && <PendingSupportBanner />}
        <dl>
          <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
            {props.showServerUrl && (
              <div className="flex flex-col gap-0.5">
                <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
                  Server URL
                </dt>
                <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 break-all">
                  {props.serverUrl || 'Not configured'}
                </dd>
              </div>
            )}
            <div className="flex flex-col gap-0.5">
              <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                Username
                {showUpdateSuccess && (
                  <div className="flex items-center gap-1.5">
                    <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                    <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Updated</p>
                  </div>
                )}
              </dt>
              <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
                {props.username || 'Not configured'}
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Password</dt>
              <dd className="text-sm/6 text-neutral-500 dark:text-neutral-400 italic">
                stored — not displayed
              </dd>
            </div>

            {!showUpdateCredentials && (
              <Headless.Button
                type="button"
                onClick={() => setShowUpdateCredentials(true)}
                className="flex text-sm/6 text-sky-600 dark:text-sky-400 underline hover:text-sky-700 dark:hover:text-sky-300 cursor-pointer"
              >
                Update credentials
              </Headless.Button>
            )}
            {showUpdateCredentials && (
              <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
                <Form onSubmit={handleUpdateCredentials}>
                  <Fieldset>
                    <FieldGroup>
                      {props.showServerUrl && (
                        <Field>
                          <Label>Server URL</Label>
                          <Input
                            placeholder="https://imageright.your-agency.com"
                            value={serverUrl}
                            onChange={(e) => setServerUrl(e.currentTarget.value)}
                            className="w-full"
                          />
                        </Field>
                      )}
                      <Field>
                        <Label>Username</Label>
                        <Input
                          placeholder="Username"
                          value={username}
                          onChange={(e) => setUsername(e.currentTarget.value)}
                          className="w-full"
                        />
                      </Field>
                      <Field>
                        <Label>Password</Label>
                        <Input
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.currentTarget.value)}
                          type="password"
                          className="w-full"
                        />
                      </Field>
                    </FieldGroup>
                  </Fieldset>
                  <div className="mt-6">
                    {error && <ErrorMessage className="mb-2">{error}</ErrorMessage>}
                    <Button type="submit" color="sky" loading={loading}>
                      Update credentials
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </div>
        </dl>
      </div>
    </div>
  );
};

const ManageImageRight = ({ integration }: { integration: IntegrationDetailFieldsFragment }) => (
  <ManageCredentialIntegration
    integration={integration}
    title="ImageRight Integration"
    description="Connect your ImageRight document management system to Cara. ImageRight requires an active AMS360 integration to function."
    username={integration.configuration?.imageRight?.username}
    serverUrl={integration.configuration?.imageRight?.serverUrl}
    showServerUrl
    buildUpdateInput={({ serverUrl, username, password }) => ({
      imageRight: { serverUrl, username, password }
    })}
  />
);

const ManageQQCatalyst = ({ integration, sentEmailLoggingSwitch }: ManageAmsProps) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const cfg = integration.configuration?.qqcatalyst;
  const [enableSyncAccounts, setEnableSyncAccounts] = React.useState(
    cfg?.enableSyncAccounts ?? true
  );
  const [enableSyncTasks, setEnableSyncTasks] = React.useState(cfg?.enableSyncTasks ?? true);
  const [enableSyncCalls, setEnableSyncCalls] = React.useState(cfg?.enableSyncCalls ?? true);
  const [defaultLocationId, setDefaultLocationId] = React.useState(
    cfg?.defaultLocationId != null ? String(cfg.defaultLocationId) : ''
  );
  const [taskAssignment, setTaskAssignment] = React.useState(
    cfg?.taskAssignment ?? QqCatalystTaskAssignment.Csr
  );
  const [taskAssignmentEmployeeId, setTaskAssignmentEmployeeId] = React.useState(
    cfg?.taskAssignmentEmployeeId != null ? String(cfg.taskAssignmentEmployeeId) : ''
  );

  React.useEffect(() => {
    setEnableSyncAccounts(cfg?.enableSyncAccounts ?? true);
    setEnableSyncTasks(cfg?.enableSyncTasks ?? true);
    setEnableSyncCalls(cfg?.enableSyncCalls ?? true);
    setDefaultLocationId(cfg?.defaultLocationId != null ? String(cfg.defaultLocationId) : '');
    setTaskAssignment(cfg?.taskAssignment ?? QqCatalystTaskAssignment.Csr);
    setTaskAssignmentEmployeeId(
      cfg?.taskAssignmentEmployeeId != null ? String(cfg.taskAssignmentEmployeeId) : ''
    );
  }, [
    cfg?.enableSyncAccounts,
    cfg?.enableSyncTasks,
    cfg?.enableSyncCalls,
    cfg?.defaultLocationId,
    cfg?.taskAssignment,
    cfg?.taskAssignmentEmployeeId
  ]);
  const [error, setError] = React.useState<string | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [showSavedAccounts, setShowSavedAccounts] = React.useState(false);
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);
  const [showSavedOffice, setShowSavedOffice] = React.useState(false);
  const [showSavedAssignment, setShowSavedAssignment] = React.useState(false);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [loadingOffice, setLoadingOffice] = React.useState(false);
  const [loadingAssignment, setLoadingAssignment] = React.useState(false);

  const markSaved = (setSaved: (v: boolean) => void) => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const locations = cfg?.locations ?? [];
  const employees = cfg?.employees ?? [];
  const [categoryCounts, setCategoryCounts] = React.useState(cfg?.categoryCounts ?? []);
  React.useEffect(() => {
    setCategoryCounts(cfg?.categoryCounts ?? []);
  }, [cfg?.categoryCounts]);
  const officeRequired = locations.length > 1;
  const officeError = getValidationError(validationErrors, ['qqcatalyst', 'defaultLocationId']);
  const employeeError = getValidationError(validationErrors, [
    'qqcatalyst',
    'taskAssignmentEmployeeId'
  ]);

  const save = async (
    input: NonNullable<UpdateIntegrationInput['qqcatalyst']>
  ): Promise<boolean> => {
    setError(null);
    setValidationErrors([]);
    try {
      const result = await updateIntegration({
        variables: { id: integration.id, input: { qqcatalyst: input } }
      });
      if (result.errors?.length) {
        throw new ApolloError({ graphQLErrors: result.errors });
      }
      return true;
    } catch (err) {
      const validation = getValidationErrors(err as ApolloError) ?? [];
      if (validation.length > 0) {
        setValidationErrors(validation);
      } else {
        setError(messageFromError(err, 'Could not save QQCatalyst settings.'));
      }
      return false;
    }
  };

  const handleSyncToggle = async (
    field: 'enableSyncAccounts' | 'enableSyncTasks' | 'enableSyncCalls',
    enabled: boolean
  ) => {
    const setters = {
      enableSyncAccounts: [setEnableSyncAccounts, setLoadingAccounts, setShowSavedAccounts],
      enableSyncTasks: [setEnableSyncTasks, setLoadingTasks, setShowSavedTasks],
      enableSyncCalls: [setEnableSyncCalls, setLoadingCalls, setShowSavedCalls]
    } as const;
    const [setValue, setLoading, setSaved] = setters[field];
    setValue(enabled);
    setLoading(true);
    if (await save({ [field]: enabled })) {
      markSaved(setSaved);
    } else {
      setValue(!enabled);
    }
    setLoading(false);
  };

  const handleOffice = async (value: string) => {
    const previous = defaultLocationId;
    setDefaultLocationId(value);
    setLoadingOffice(true);
    if (await save({ defaultLocationId: value ? Number(value) : null })) {
      markSaved(setShowSavedOffice);
    } else {
      setDefaultLocationId(previous);
    }
    setLoadingOffice(false);
  };

  const handleAssignment = async (method: QqCatalystTaskAssignment, employeeId: string) => {
    const previousMethod = taskAssignment;
    const previousEmployee = taskAssignmentEmployeeId;
    setTaskAssignment(method);
    setTaskAssignmentEmployeeId(employeeId);
    setLoadingAssignment(true);
    const saved = await save({
      taskAssignment: method,
      taskAssignmentEmployeeId:
        method === QqCatalystTaskAssignment.SpecifiedEmployee && employeeId
          ? Number(employeeId)
          : null
    });
    if (saved) {
      markSaved(setShowSavedAssignment);
    } else {
      setTaskAssignment(previousMethod);
      setTaskAssignmentEmployeeId(previousEmployee);
    }
    setLoadingAssignment(false);
  };

  const savedMark = (show: boolean) =>
    show ? (
      <div className="flex items-center gap-1.5">
        <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
        <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
      </div>
    ) : null;

  return (
    <div>
      <QQCatalystOutcomeBanner className="mb-4" />
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          QQCatalyst Integration
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Cara creates new prospects, notes, and tasks in the office you select. Sync Now indexes the
        book Cara can see for this connection.
      </p>
      {error && <ErrorMessage className="mt-4">{error}</ErrorMessage>}
      {locations.length > 0 && (
        <Field className="mt-4">
          <div className="flex items-center gap-2">
            <Label>Default office</Label>
            {savedMark(showSavedOffice)}
          </div>
          <Description>
            Cara creates new prospects, notes, and tasks in this office.
            {officeRequired ? ' Required because more than one office was found.' : ''}
          </Description>
          <Select
            value={defaultLocationId}
            onChange={(e) => handleOffice(e.currentTarget.value)}
            disabled={loadingOffice}
            invalid={!!officeError}
          >
            <option value="">
              {officeRequired ? 'Select an office' : 'Use QQCatalyst default'}
            </option>
            {locations.map((loc) => (
              <option key={loc.locationId} value={String(loc.locationId)}>
                {loc.locationName}
              </option>
            ))}
          </Select>
          {officeError && <ErrorMessage>{officeError}</ErrorMessage>}
        </Field>
      )}
      <div className="mt-4 space-y-4">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Sync Settings</h3>
        <SwitchField>
          <div className="flex items-center gap-2" data-slot="label">
            <Label>Create prospects in QQCatalyst</Label>
            {savedMark(showSavedAccounts)}
          </div>
          <Description>
            If enabled, Cara will create prospects for callers that do not already exist. Customers
            are never auto-created.
          </Description>
          <Switch
            checked={enableSyncAccounts}
            onChange={(enabled) => handleSyncToggle('enableSyncAccounts', enabled)}
            disabled={loadingAccounts}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2" data-slot="label">
            <Label>Enable Sync Calls</Label>
            {savedMark(showSavedCalls)}
          </div>
          <Description>Sync call notes from Cara to QQCatalyst.</Description>
          <Switch
            checked={enableSyncCalls}
            onChange={(enabled) => handleSyncToggle('enableSyncCalls', enabled)}
            disabled={loadingCalls}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2" data-slot="label">
            <Label>Enable Sync Tasks</Label>
            {savedMark(showSavedTasks)}
          </div>
          <Description>Sync tasks from Cara to QQCatalyst.</Description>
          <Switch
            checked={enableSyncTasks}
            onChange={(enabled) => handleSyncToggle('enableSyncTasks', enabled)}
            disabled={loadingTasks}
            color="sky"
          />
        </SwitchField>
        {sentEmailLoggingSwitch}
        <Field>
          <div data-slot="label" className="flex items-center gap-2">
            <Label>Task assignment</Label>
            {savedMark(showSavedAssignment)}
          </div>
          <Description>Who synced tasks are assigned to. Default is CSR.</Description>
          <Select
            value={taskAssignment}
            disabled={loadingAssignment || !enableSyncTasks}
            onChange={(e) =>
              handleAssignment(e.target.value as QqCatalystTaskAssignment, taskAssignmentEmployeeId)
            }
          >
            <option value={QqCatalystTaskAssignment.Csr}>CSR (default)</option>
            <option value={QqCatalystTaskAssignment.Agent}>Agent</option>
            <option value={QqCatalystTaskAssignment.SpecifiedEmployee}>Specific employee</option>
          </Select>
        </Field>
        {(taskAssignment === QqCatalystTaskAssignment.SpecifiedEmployee || employeeError) && (
          <Field>
            <Label>Employee</Label>
            <Description>Tasks are assigned to this QQCatalyst employee.</Description>
            <Select
              value={taskAssignmentEmployeeId}
              disabled={loadingAssignment || !enableSyncTasks}
              invalid={!!employeeError}
              onChange={(e) =>
                handleAssignment(QqCatalystTaskAssignment.SpecifiedEmployee, e.currentTarget.value)
              }
            >
              <option value="">Select an employee</option>
              {employees.map((emp) => (
                <option key={emp.employeeId} value={String(emp.employeeId)}>
                  {emp.displayName}
                </option>
              ))}
            </Select>
            {employeeError && <ErrorMessage>{employeeError}</ErrorMessage>}
          </Field>
        )}
      </div>
      {categoryCounts.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
            QQCatalyst statistics
          </h3>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            These counts come from QQCatalyst and may not include every record. They are not
            complete book totals; a zero does not mean no records exist.
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
            {categoryCounts
              .filter((row) => !['BlobInfo', 'BlobContainerInfo'].includes(row.category))
              .map((row) => (
                <div key={row.category} className="flex justify-between gap-2 text-sm">
                  <dt className="text-neutral-600 dark:text-neutral-400">{row.category}</dt>
                  <dd className="font-medium text-neutral-900 dark:text-white">{row.count}</dd>
                </div>
              ))}
          </dl>
        </div>
      )}
      <div className="mt-4">
        <IntegrationSyncLogs
          integration={integration}
          onBookSize={(counts) => setCategoryCounts(counts ?? [])}
        />
      </div>
    </div>
  );
};

const ManageAMS360 = ({ integration, sentEmailLoggingSwitch }: ManageAmsProps) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [showSavedAccounts, setShowSavedAccounts] = React.useState(false);
  const [enableSyncAccounts, setEnableSyncAccounts] = React.useState(
    (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncAccounts ?? true
  );
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);
  const [enableSyncCalls, setEnableSyncCalls] = React.useState(
    (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncCalls ?? true
  );
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [enableSyncTasks, setEnableSyncTasks] = React.useState(
    (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncTasks ?? true
  );
  const [loadingAssignment, setLoadingAssignment] = React.useState(false);
  const [showSavedAssignment, setShowSavedAssignment] = React.useState(false);
  const [taskAssignmentMethod, setTaskAssignmentMethod] =
    React.useState<Ams360TaskAssignmentMethod>(
      (integration.configuration as IntegrationConfiguration)?.ams360?.taskAssignmentMethod ??
        Ams360TaskAssignmentMethod.AccountExecutive
    );
  const [loadingDueBusinessDays, setLoadingDueBusinessDays] = React.useState(false);
  const [showSavedDueBusinessDays, setShowSavedDueBusinessDays] = React.useState(false);
  const configuredDueBusinessDays = (integration.configuration as IntegrationConfiguration)?.ams360
    ?.defaultTaskDueBusinessDays;
  const [defaultTaskDueBusinessDays, setDefaultTaskDueBusinessDays] = React.useState<string>(
    configuredDueBusinessDays != null ? String(configuredDueBusinessDays) : ''
  );

  React.useEffect(() => {
    setEnableSyncAccounts(
      (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncAccounts ?? true
    );
    setEnableSyncCalls(
      (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncCalls ?? true
    );
    setEnableSyncTasks(
      (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncTasks ?? true
    );
    setTaskAssignmentMethod(
      (integration.configuration as IntegrationConfiguration)?.ams360?.taskAssignmentMethod ??
        Ams360TaskAssignmentMethod.AccountExecutive
    );
    const days = (integration.configuration as IntegrationConfiguration)?.ams360
      ?.defaultTaskDueBusinessDays;
    setDefaultTaskDueBusinessDays(days != null ? String(days) : '');
  }, [
    (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncAccounts,
    (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncCalls,
    (integration.configuration as IntegrationConfiguration)?.ams360?.enableSyncTasks,
    (integration.configuration as IntegrationConfiguration)?.ams360?.taskAssignmentMethod,
    (integration.configuration as IntegrationConfiguration)?.ams360?.defaultTaskDueBusinessDays
  ]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (defaultTaskDueBusinessDays === '') {
        return;
      }
      handleDefaultTaskDueBusinessDaysBlur();
    }, 600);

    return () => clearTimeout(timer);
  }, [defaultTaskDueBusinessDays]);

  const handleAssignmentMethodChange = (method: Ams360TaskAssignmentMethod) =>
    handleSave(
      setTaskAssignmentMethod,
      setLoadingAssignment,
      setShowSavedAssignment,
      method,
      taskAssignmentMethod,
      { taskAssignmentMethod: method }
    );

  const handleDefaultTaskDueBusinessDaysBlur = () => {
    const trimmed = defaultTaskDueBusinessDays.trim();
    if (trimmed === '') {
      setDefaultTaskDueBusinessDays(
        configuredDueBusinessDays != null ? String(configuredDueBusinessDays) : ''
      );
      return;
    }

    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setDefaultTaskDueBusinessDays(
        configuredDueBusinessDays != null ? String(configuredDueBusinessDays) : ''
      );
      return;
    }

    if (configuredDueBusinessDays === parsed) {
      setDefaultTaskDueBusinessDays(String(parsed));
      return;
    }

    handleSave(
      (value: string) => setDefaultTaskDueBusinessDays(value),
      setLoadingDueBusinessDays,
      setShowSavedDueBusinessDays,
      String(parsed),
      configuredDueBusinessDays != null ? String(configuredDueBusinessDays) : '',
      { defaultTaskDueBusinessDays: parsed }
    );
  };

  const handleSyncToggle = async (
    type: 'enableSyncAccounts' | 'enableSyncCalls' | 'enableSyncTasks',
    enabled: boolean
  ) => {
    if (type === 'enableSyncAccounts') {
      handleSave(
        setEnableSyncAccounts,
        setLoadingAccounts,
        setShowSavedAccounts,
        enabled,
        !enabled,
        {
          enableSyncAccounts: enabled
        }
      );
    } else if (type === 'enableSyncCalls') {
      handleSave(setEnableSyncCalls, setLoadingCalls, setShowSavedCalls, enabled, !enabled, {
        enableSyncCalls: enabled
      });
    } else if (type === 'enableSyncTasks') {
      handleSave(setEnableSyncTasks, setLoadingTasks, setShowSavedTasks, enabled, !enabled, {
        enableSyncTasks: enabled
      });
    }
  };

  // handleSave optimistically applies a config value, persists it, and restores the
  // previous value if the mutation fails. Shared by the sync toggles and the assignment
  // method select.
  const handleSave = async <T,>(
    setValue: (v: T) => void,
    setLoading: (v: boolean) => void,
    setSaved: (v: boolean) => void,
    newValue: T,
    previousValue: T,
    input: UpdateAms360IntegrationInput
  ) => {
    setValue(newValue);
    setLoading(true);
    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: { ams360: input }
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setValue(previousValue);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          AMS360 Integration
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect your AMS360 AMS to Cara to automatically retrieve, index, and search data from your
        agency management system.
      </p>
      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">App Key</dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {integration.configuration?.ams360?.appKey ? '••••••••••••••••' : 'Not configured'}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
              Agency Number
            </dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {integration.configuration?.ams360?.agencyNo || 'Not configured'}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
              App Access to Agency Key
            </dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {integration.configuration?.ams360?.appAccessToAgencyKey
                ? '••••••••••••••••'
                : 'Not configured'}
            </dd>
          </div>
        </div>
      </dl>

      <div className="mt-2 flex flex-col gap-6">
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Create accounts in AMS360</Label>
            {showSavedAccounts && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>
            If enabled, Cara will create new client records in AMS360 for callers not already in
            your system. If disabled, Cara will not create tasks or notes for clients that don't
            already exist in AMS360.
          </Description>
          <Switch
            checked={enableSyncAccounts}
            onChange={(enabled) => handleSyncToggle('enableSyncAccounts', enabled)}
            disabled={loadingAccounts}
            color="sky"
          />
        </SwitchField>

        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Sync calls to AMS360</Label>
            {showSavedCalls && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>If enabled, Cara will sync calls to AMS360 as activities.</Description>
          <Switch
            checked={enableSyncCalls}
            onChange={(enabled) => handleSyncToggle('enableSyncCalls', enabled)}
            disabled={loadingCalls}
            color="sky"
          />
        </SwitchField>

        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Sync tasks to AMS360</Label>
            {showSavedTasks && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>If enabled, Cara will sync tasks to AMS360 as suspenses.</Description>
          <Switch
            checked={enableSyncTasks}
            onChange={(enabled) => handleSyncToggle('enableSyncTasks', enabled)}
            disabled={loadingTasks}
            color="sky"
          />
        </SwitchField>
        {sentEmailLoggingSwitch}

        <Field>
          <div data-slot="label" className="flex items-center gap-2">
            <Label>Task assignment</Label>
            {showSavedAssignment && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>
            Who synced tasks (suspenses) are assigned to. Account CSR uses the account manager on
            the account; the default is the account executive.
          </Description>
          <Select
            value={taskAssignmentMethod}
            disabled={loadingAssignment || !enableSyncTasks}
            onChange={(e) =>
              handleAssignmentMethodChange(e.target.value as Ams360TaskAssignmentMethod)
            }
          >
            <option value={Ams360TaskAssignmentMethod.AccountExecutive}>
              Account executive (default)
            </option>
            <option value={Ams360TaskAssignmentMethod.AccountCsr}>Account CSR</option>
          </Select>
        </Field>

        <Field>
          <div data-slot="label" className="flex items-center gap-2">
            <Label>Default task due date</Label>
            {showSavedDueBusinessDays && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>
            When a task has no specific due date, the number of business days after task creation
            that the task is due (weekends skipped). Defaults to 3 when unset.
          </Description>
          <Input
            type="number"
            min={1}
            value={defaultTaskDueBusinessDays}
            disabled={loadingDueBusinessDays || !enableSyncTasks}
            placeholder="3"
            onChange={(e) => setDefaultTaskDueBusinessDays(e.currentTarget.value)}
            onBlur={handleDefaultTaskDueBusinessDaysBlur}
          />
        </Field>
      </div>

      <div className="my-4">
        <IntegrationSyncLogs integration={integration} />
      </div>
    </div>
  );
};

const ManageAgencyZoom = ({ integration }: { integration: IntegrationDetailFieldsFragment }) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [enableSyncAccounts, setEnableSyncAccounts] = React.useState(
    (integration.configuration as IntegrationConfiguration)?.agencyZoom?.enableSyncAccounts || false
  );
  const [enableSyncTasks, setEnableSyncTasks] = React.useState(
    (integration.configuration as IntegrationConfiguration)?.agencyZoom?.enableSyncTasks || false
  );
  const [showSavedAccounts, setShowSavedAccounts] = React.useState(false);
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);

  const [showUpdateCredentials, setShowUpdateCredentials] = React.useState(false);
  const [showUpdateCredentialsSuccess, setShowUpdateCredentialsSuccess] = React.useState(false);
  const [username, setUsername] = React.useState<string>(
    integration.configuration?.agencyZoom?.username || ''
  );
  const [password, setPassword] = React.useState<string>();
  const [isSSOLogin, setIsSSOLogin] = React.useState(
    (integration.configuration as IntegrationConfiguration)?.agencyZoom?.isSSOLogin || false
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();

  // Update local state when integration prop changes
  React.useEffect(() => {
    setEnableSyncAccounts(
      (integration.configuration as IntegrationConfiguration)?.agencyZoom?.enableSyncAccounts ||
        false
    );
    setEnableSyncTasks(
      (integration.configuration as IntegrationConfiguration)?.agencyZoom?.enableSyncTasks || false
    );
  }, [
    (integration.configuration as IntegrationConfiguration)?.agencyZoom?.enableSyncAccounts,
    (integration.configuration as IntegrationConfiguration)?.agencyZoom?.enableSyncTasks
  ]);

  // Handler for sync toggle
  const handleSyncToggle = async (enabled: boolean) => {
    setEnableSyncTasks(enabled);
    setLoadingTasks(true);

    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            agencyZoom: {
              enableSyncTasks: enabled
            }
          }
        }
      });
      setShowSavedTasks(true);
      setTimeout(() => setShowSavedTasks(false), 2000);
    } catch {
      setEnableSyncTasks(!enabled);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Handler for accounts toggle
  const handleAccountsToggle = async (enabled: boolean) => {
    setEnableSyncAccounts(enabled);
    setLoadingAccounts(true);
    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            agencyZoom: { enableSyncAccounts: enabled }
          }
        }
      });
      setShowSavedAccounts(true);
      setTimeout(() => setShowSavedAccounts(false), 2000);
    } catch {
      setEnableSyncAccounts(!enabled);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleUpdateCredentials = async () => {
    try {
      setLoading(true);
      const result = await updateIntegration({
        variables: {
          id: integration.id,
          input: { agencyZoom: { username, password, isSSOLogin } }
        }
      });
      if (result.errors?.length) {
        setError(result.errors[0].message);
      } else {
        setShowUpdateCredentialsSuccess(true);
        setShowUpdateCredentials(false);
        setTimeout(() => setShowUpdateCredentialsSuccess(false), 2000);
      }
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          AgencyZoom Integration
        </h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect your AgencyZoom AMS to Cara to automatically retrieve, index, and search data from
        your agency management system and write activity back.
      </p>
      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white flex items-center gap-2">
              Username
              {showUpdateCredentialsSuccess && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                  <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Updated</p>
                </div>
              )}
            </dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              {username || 'Not configured'}
            </dd>
          </div>
          {!showUpdateCredentials && (
            <Headless.Button
              type="button"
              onClick={() => setShowUpdateCredentials(true)}
              className="flex text-sm/6 text-sky-600 dark:text-sky-400 underline hover:text-sky-700 dark:hover:text-sky-300 cursor-pointer"
            >
              Update credentials
            </Headless.Button>
          )}
          {showUpdateCredentials && (
            <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
              <Form onSubmit={handleUpdateCredentials}>
                <Fieldset>
                  <FieldGroup>
                    <div className="flex flex-row gap-4">
                      <Field className="w-full">
                        <Label>Username</Label>
                        <Input
                          placeholder="Username"
                          value={username}
                          onChange={(e) => setUsername(e.currentTarget.value)}
                          className="w-full"
                        />
                      </Field>
                      <Field className="w-full">
                        <Label>Password</Label>
                        <Input
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.currentTarget.value)}
                          type="password"
                          className="w-full"
                        />
                      </Field>
                    </div>
                    <SwitchField>
                      <Label>Use Vertafore SSO</Label>
                      <Description>
                        Enable if you use Vertafore SSO to sign into AgencyZoom.
                      </Description>
                      <Switch
                        checked={isSSOLogin}
                        onChange={(enabled) => setIsSSOLogin(enabled)}
                        color="sky"
                      />
                    </SwitchField>
                  </FieldGroup>
                </Fieldset>
                <div className="mt-6">
                  {error && <ErrorMessage className="mb-2">{error}</ErrorMessage>}
                  <Button type="submit" color="sky" loading={loading}>
                    Update credentials
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </div>
      </dl>

      <div className="mt-4 space-y-4">
        <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Sync Settings</h3>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Create accounts in AgencyZoom</Label>
            {showSavedAccounts && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>
            If enabled, Cara will create accounts for clients that don't exist. If disabled, Cara
            will not write data for clients that don't already exist in AgencyZoom.
          </Description>
          <Switch
            checked={enableSyncAccounts}
            onChange={(enabled) => handleAccountsToggle(enabled)}
            disabled={loadingAccounts}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Tasks</Label>
            {showSavedTasks && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync tasks from Cara to AgencyZoom.</Description>
          <Switch
            checked={enableSyncTasks}
            onChange={(enabled) => handleSyncToggle(enabled)}
            disabled={loadingTasks}
            color="sky"
          />
        </SwitchField>
      </div>

      <div className="mt-4">
        <IntegrationSyncLogs integration={integration} hideSyncButton />
      </div>
    </div>
  );
};

const ManageHubSpot = ({ integration }: { integration: IntegrationDetailFieldsFragment }) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [showSavedAccounts, setShowSavedAccounts] = React.useState(false);
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);

  // Add query for integration state
  const { data: integrationData, refetch: refetchIntegration } = useGetIntegrationStateQuery({
    variables: { id: integration.id }
  });

  const [taskConfig, setTaskConfig] = React.useState({
    enabled: integration.configuration?.hubspot?.enableSyncTasks || false,
    syncType:
      integration.configuration?.hubspot?.taskSyncType || HubSpotTaskSyncType.EngagementTask,
    pipelineId: integration.configuration?.hubspot?.taskPipelineId || '',
    stageId: integration.configuration?.hubspot?.taskStageId || ''
  });

  const [accountConfig, setAccountConfig] = React.useState({
    enabled: integration.configuration?.hubspot?.enableSyncAccounts || false
  });

  const [callConfig, setCallConfig] = React.useState({
    enabled: integration.configuration?.hubspot?.enableSyncCalls || false,
    syncType:
      integration.configuration?.hubspot?.callSyncType || HubSpotCallSyncType.EngagementCall,
    pipelineId: integration.configuration?.hubspot?.callPipelineId || '',
    stageId: integration.configuration?.hubspot?.callStageId || ''
  });

  // Update local state when integration prop changes
  React.useEffect(() => {
    setTaskConfig({
      enabled: integration.configuration?.hubspot?.enableSyncTasks || false,
      syncType:
        integration.configuration?.hubspot?.taskSyncType || HubSpotTaskSyncType.EngagementTask,
      pipelineId: integration.configuration?.hubspot?.taskPipelineId || '',
      stageId: integration.configuration?.hubspot?.taskStageId || ''
    });
    setAccountConfig({
      enabled: integration.configuration?.hubspot?.enableSyncAccounts || false
    });
    setCallConfig({
      enabled: integration.configuration?.hubspot?.enableSyncCalls || false,
      syncType:
        integration.configuration?.hubspot?.callSyncType || HubSpotCallSyncType.EngagementCall,
      pipelineId: integration.configuration?.hubspot?.callPipelineId || '',
      stageId: integration.configuration?.hubspot?.callStageId || ''
    });
  }, [integration.configuration?.hubspot]);

  // Create a version of the integration with the latest state
  const currentIntegration = {
    ...integration,
    state: integrationData?.integration?.state || integration.state
  };

  const handleTaskConfigUpdate = async (updates: Partial<typeof taskConfig>) => {
    setLoadingTasks(true);
    const newConfig = { ...taskConfig, ...updates };
    setTaskConfig(newConfig);

    try {
      // Update HubSpot config - let API handle state transitions
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            hubspot: {
              enableSyncTasks: newConfig.enabled,
              taskSyncType: newConfig.enabled ? newConfig.syncType : undefined,
              taskPipelineId:
                newConfig.enabled && newConfig.syncType === HubSpotTaskSyncType.Ticket
                  ? newConfig.pipelineId
                  : undefined,
              taskStageId:
                newConfig.enabled && newConfig.syncType === HubSpotTaskSyncType.Ticket
                  ? newConfig.stageId
                  : undefined
            }
          }
        }
      });

      // Refetch integration state to ensure UI is in sync with database
      await refetchIntegration();

      setShowSavedTasks(true);
      setTimeout(() => setShowSavedTasks(false), 2000);
    } catch {
      setTaskConfig(taskConfig); // Revert on error
      // Also refetch integration state in case of error
      await refetchIntegration();
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleAccountConfigUpdate = async (updates: Partial<typeof accountConfig>) => {
    setLoadingAccounts(true);
    const newConfig = { ...accountConfig, ...updates };
    setAccountConfig(newConfig);

    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            hubspot: {
              enableSyncAccounts: newConfig.enabled
            }
          }
        }
      });

      await refetchIntegration();

      setShowSavedAccounts(true);
      setTimeout(() => setShowSavedAccounts(false), 2000);
    } catch {
      setAccountConfig(accountConfig);
      await refetchIntegration();
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleCallConfigUpdate = async (updates: Partial<typeof callConfig>) => {
    setLoadingCalls(true);
    const newConfig = { ...callConfig, ...updates };
    setCallConfig(newConfig);

    try {
      // First update the HubSpot config with default field mappings
      const defaultCallFieldMappings =
        newConfig.enabled && newConfig.syncType === HubSpotCallSyncType.Ticket
          ? {
              ticketFields: [
                { hubspotField: 'content', caraField: 'summary' },
                { hubspotField: 'content', caraField: 'transcription' },
                { hubspotField: 'subject', caraField: 'callFromName' }
              ],
              dealFields: [] // Required by type but not used for ticket sync
            }
          : undefined;

      // Update HubSpot config - let API handle state transitions
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            hubspot: {
              enableSyncCalls: newConfig.enabled,
              callSyncType: newConfig.enabled ? newConfig.syncType : undefined,
              callPipelineId:
                newConfig.enabled && newConfig.syncType === HubSpotCallSyncType.Ticket
                  ? newConfig.pipelineId
                  : undefined,
              callStageId:
                newConfig.enabled && newConfig.syncType === HubSpotCallSyncType.Ticket
                  ? newConfig.stageId
                  : undefined,
              callFieldMappings: defaultCallFieldMappings
            }
          }
        }
      });

      // Refetch integration state to ensure UI is in sync with database
      await refetchIntegration();

      setShowSavedCalls(true);
      setTimeout(() => setShowSavedCalls(false), 2000);
    } catch {
      setCallConfig(callConfig); // Revert on error
      // Also refetch integration state in case of error
      await refetchIntegration();
    } finally {
      setLoadingCalls(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          HubSpot Integration
        </h2>
        <Badge
          color={
            integrationData?.integration?.state === IntegrationState.Active
              ? 'green'
              : integrationData?.integration?.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {titleCase(integrationData?.integration?.state || integration.state)}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Configure how Cara syncs tasks and calls with your HubSpot CRM.
      </p>

      <div className="mt-6 space-y-6">
        {/* Account Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
            Account Sync Settings
          </h3>
          <SwitchField>
            <div className="flex items-center gap-2">
              <Label>Create accounts in HubSpot</Label>
              {showSavedAccounts && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                  <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
                </div>
              )}
            </div>
            <Description>
              If enabled, Cara will create accounts for clients that don't exist. If disabled, Cara
              will not write data for clients that don't already exist in HubSpot.
            </Description>
            <Switch
              checked={accountConfig.enabled}
              onChange={(enabled) => handleAccountConfigUpdate({ enabled })}
              disabled={loadingAccounts}
              color="sky"
            />
          </SwitchField>
        </div>

        {/* Task Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
            Task Sync Settings
          </h3>
          <SwitchField>
            <div className="flex items-center gap-2">
              <Label>Enable Task Sync</Label>
              {showSavedTasks && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                  <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
                </div>
              )}
            </div>
            <Description>Sync tasks from Cara to HubSpot.</Description>
            <Switch
              checked={taskConfig.enabled}
              onChange={(enabled) => handleTaskConfigUpdate({ enabled })}
              disabled={loadingTasks}
              color="sky"
            />
          </SwitchField>

          {taskConfig.enabled && (
            <div className="space-y-4 pl-4 border-l-2 border-neutral-100 dark:border-neutral-700">
              <Field>
                <Label>Sync Type</Label>
                <Description>Choose how tasks should be created in HubSpot.</Description>
                <select
                  className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-neutral-900 dark:text-white ring-1 ring-inset ring-neutral-300 dark:ring-neutral-600 focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6"
                  value={taskConfig.syncType}
                  onChange={(e) =>
                    handleTaskConfigUpdate({ syncType: e.target.value as HubSpotTaskSyncType })
                  }
                  disabled={loadingTasks}
                >
                  <option value={HubSpotTaskSyncType.EngagementTask}>Task Engagement</option>
                  <option value={HubSpotTaskSyncType.Ticket}>Ticket</option>
                </select>
              </Field>

              {taskConfig.syncType === HubSpotTaskSyncType.Ticket && (
                <>
                  <Field>
                    <Label>Ticket Pipeline</Label>
                    <Description>Select the pipeline to create tickets in.</Description>
                    <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                      <HubSpotTicketPipelineSelect
                        integrationId={integration.id}
                        value={taskConfig.pipelineId}
                        onChange={(pipelineId) =>
                          handleTaskConfigUpdate({ pipelineId, stageId: '' })
                        }
                        disabled={loadingTasks}
                      />
                    </React.Suspense>
                  </Field>
                  {taskConfig.pipelineId && (
                    <Field>
                      <Label>Ticket Stage</Label>
                      <Description>Select the stage to create tickets in.</Description>
                      <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                        <HubSpotTicketStageSelect
                          integrationId={integration.id}
                          pipelineId={taskConfig.pipelineId}
                          value={taskConfig.stageId}
                          onChange={(stageId) => handleTaskConfigUpdate({ stageId })}
                          disabled={loadingTasks}
                        />
                      </React.Suspense>
                    </Field>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Call Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-neutral-900 dark:text-white">
            Call Sync Settings
          </h3>
          <SwitchField>
            <div className="flex items-center gap-2">
              <Label>Enable Call Sync</Label>
              {showSavedCalls && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500 dark:text-neutral-400" />
                  <p className="text-xs/6 text-neutral-500 dark:text-neutral-400">Saved</p>
                </div>
              )}
            </div>
            <Description>Sync calls from Cara to HubSpot.</Description>
            <Switch
              checked={callConfig.enabled}
              onChange={(enabled) => handleCallConfigUpdate({ enabled })}
              disabled={loadingCalls}
              color="sky"
            />
          </SwitchField>

          {callConfig.enabled && (
            <div className="space-y-4 pl-4 border-l-2 border-neutral-100 dark:border-neutral-700">
              <Field>
                <Label>Sync Type</Label>
                <Description>Choose how calls should be created in HubSpot.</Description>
                <select
                  className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-neutral-900 dark:text-white ring-1 ring-inset ring-neutral-300 dark:ring-neutral-600 focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6"
                  value={callConfig.syncType}
                  onChange={(e) =>
                    handleCallConfigUpdate({ syncType: e.target.value as HubSpotCallSyncType })
                  }
                  disabled={loadingCalls}
                >
                  <option value={HubSpotCallSyncType.EngagementCall}>Call Engagement</option>
                  <option value={HubSpotCallSyncType.EngagementNote}>Note Engagement</option>
                  <option value={HubSpotCallSyncType.Ticket}>Ticket</option>
                </select>
              </Field>

              {callConfig.syncType === HubSpotCallSyncType.Ticket && (
                <>
                  <Field>
                    <Label>Ticket Pipeline</Label>
                    <Description>Select the pipeline to create tickets in.</Description>
                    <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                      <HubSpotTicketPipelineSelect
                        integrationId={integration.id}
                        value={callConfig.pipelineId}
                        onChange={(pipelineId) =>
                          handleCallConfigUpdate({ pipelineId, stageId: '' })
                        }
                        disabled={loadingCalls}
                      />
                    </React.Suspense>
                  </Field>
                  {callConfig.pipelineId && (
                    <Field>
                      <Label>Ticket Stage</Label>
                      <Description>Select the stage to create tickets in.</Description>
                      <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                        <HubSpotTicketStageSelect
                          integrationId={integration.id}
                          pipelineId={callConfig.pipelineId}
                          value={callConfig.stageId}
                          onChange={(stageId) => handleCallConfigUpdate({ stageId })}
                          disabled={loadingCalls}
                        />
                      </React.Suspense>
                    </Field>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <IntegrationSyncLogs integration={currentIntegration} />
      </div>
    </div>
  );
};

const ManageZapier = ({ integration }: { integration: IntegrationDetailFieldsFragment }) => {
  const [showApiKey, setShowApiKey] = React.useState(false);
  const [showCopiedTooltip, setShowCopiedTooltip] = React.useState(false);
  const [isPolling, setIsPolling] = React.useState(false);

  // Add query for integration state
  const { data: integrationData, refetch: refetchIntegration } = useGetIntegrationStateQuery({
    variables: { id: integration.id }
  });

  const handleCopy = async () => {
    if (integration.configuration?.zapier?.apiKey) {
      try {
        await navigator.clipboard.writeText(integration.configuration.zapier.apiKey);
        setShowCopiedTooltip(true);
        setTimeout(() => setShowCopiedTooltip(false), 2000);
      } catch {
        // Silently handle clipboard errors
      }
    }
  };

  // Poll for integration state changes when user clicks "Add Integration"
  React.useEffect(() => {
    if (isPolling) {
      const interval = setInterval(async () => {
        await refetchIntegration();
        // Stop polling if integration becomes active
        if (integrationData?.integration?.state === IntegrationState.Active) {
          setIsPolling(false);
        }
      }, 2000); // Check every 2 seconds

      return () => clearInterval(interval);
    }
  }, [isPolling, refetchIntegration, integrationData?.integration?.state]);

  const apiKey = integration.configuration?.zapier?.apiKey;
  const maskedApiKey = apiKey ? '•'.repeat(Math.min(apiKey.length, 32)) : 'Loading...';

  // Create a version of the integration with the latest state
  const currentIntegration = {
    ...integration,
    state: integrationData?.integration?.state || integration.state
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
          Zapier Integration
        </h2>
        <Badge
          color={
            integrationData?.integration?.state === IntegrationState.Active
              ? 'green'
              : integrationData?.integration?.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {titleCase(integrationData?.integration?.state || integration.state)}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        Connect Zapier to automate workflows between Cara and your favorite apps. Cara will
        automatically generate an API key for this integration.
      </p>

      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">API Key</dt>
            <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
              <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-neutral-700 dark:text-neutral-300 break-all">
                    {showApiKey ? apiKey : maskedApiKey}
                  </code>
                  <div className="flex items-center gap-2 ml-2">
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 p-1 rounded"
                      title={showApiKey ? 'Hide API key' : 'Show API key'}
                    >
                      {showApiKey ? (
                        <EyeSlashIcon className="size-4" />
                      ) : (
                        <EyeIcon className="size-4" />
                      )}
                    </button>
                    <div className="relative">
                      <button
                        onClick={handleCopy}
                        className="text-neutral-600 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 p-1 rounded"
                        title="Copy API key"
                      >
                        <ClipboardIcon className="size-4" />
                      </button>
                      {showCopiedTooltip && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-neutral-900 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
                          Copied!
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-neutral-900"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </dd>
          </div>
        </div>
      </dl>

      <div className="mt-4">
        <PrimaryButton
          className="w-full"
          color="white"
          onClick={() => {
            // Start polling for state changes
            setIsPolling(true);
            // Open Zapier integration page
            window.open(
              'https://zapier.com/engine/auth/start/App228677CLIAPI@1.0.0',
              '_blank',
              'width=1200,height=800,scrollbars=yes,resizable=yes'
            );
          }}
          disabled={integrationData?.integration?.state === IntegrationState.Active}
        >
          Add Integration
        </PrimaryButton>
      </div>

      <div className="mt-8">
        <IntegrationSyncLogs integration={currentIntegration} hideSyncButton />
      </div>
    </div>
  );
};

const InProgressIndicator = () => (
  <div className="relative size-2.5">
    <svg
      viewBox="0 0 10 10"
      aria-hidden="true"
      className="size-2.5 absolute top-0 left-0 z-0 animate-ping"
    >
      <circle r={5} cx={5} cy={5} className="fill-blue-300 opacity-95" />
    </svg>
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0">
      <circle r={4} cx={5} cy={5} className="fill-blue-400" />
    </svg>
  </div>
);

const CompletedIndicator = () => (
  <div className="relative size-2.5">
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0 z-0">
      <circle r={5} cx={5} cy={5} className="fill-green-300" />
    </svg>
  </div>
);

const FailedIndicator = () => (
  <div className="relative size-2.5">
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0 z-0">
      <circle r={5} cx={5} cy={5} className="fill-red-300" />
    </svg>
  </div>
);

const relativeDate = (now: Date, date: Date) => {
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);

  // Put the absolute date if the date is more than 7 days ago
  if (Math.abs(diffInSeconds) >= 604800) {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(date);
  }

  // Otherwise, show the relative date
  const units = [
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
    { unit: 'second', seconds: 1 }
  ];

  for (const { unit, seconds } of units) {
    if (Math.abs(diffInSeconds) >= seconds) {
      return rtf.format(Math.round(diffInSeconds / seconds), unit as Intl.RelativeTimeFormatUnit);
    }
  }

  return rtf.format(0, 'second');
};

interface IntegrationSyncLogsProps {
  integration: IntegrationDetailFieldsFragment;
  hideSyncButton?: boolean;
  onBookSize?: (counts: Array<QqCatalystCategoryCount> | null | undefined) => void;
}

const IntegrationSyncLogs = ({
  integration,
  hideSyncButton,
  onBookSize
}: IntegrationSyncLogsProps) => {
  const [loading, setLoading] = React.useState(false);
  const [logs, setLogs] = React.useState<IntegrationSyncLogFieldsFragment[]>(integration.syncLogs);
  const [lastSyncLogId, setLastSyncLogId] = React.useState<string>(logs?.[0]?.id);
  const [now, setNow] = React.useState(new Date());

  const canSync = usePermissions().hasAllPermissions(SYNC_INTEGRATION);

  const syncIntegration = async () => {
    setLoading(true);

    const res = await client.mutate<SyncIntegrationMutation>({
      mutation: SyncIntegrationDocument,
      variables: {
        id: integration.id
      }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    const id = res.data?.syncIntegration?.id;
    if (!id) {
      throw new Error('Failed to sync integration');
    }

    setLogs(res.data?.syncIntegration?.syncLogs || []);
    setNow(new Date());
  };

  const getIntegration = async () => {
    const res = await client.query<GetIntegrationQuery>({
      query: GetIntegrationDocument,
      variables: { id: integration.id },
      fetchPolicy: 'network-only'
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    setLogs(res.data?.integration?.syncLogs || []);
    onBookSize?.(res.data?.integration?.configuration?.qqcatalyst?.categoryCounts);
    setNow(new Date());
  };

  // Update logs when the integration is updated
  React.useEffect(() => {
    setLogs(integration.syncLogs);
  }, [integration.syncLogs]);

  // Set loading state to false when the last sync log id changes
  React.useEffect(() => {
    setLoading(loading && logs?.[0]?.id === lastSyncLogId);
    setLastSyncLogId(logs?.[0]?.id);
    setNow(new Date());
  }, [loading, logs?.[0]?.id, lastSyncLogId]);

  // Poll integration for updates while the most recent sync log is in progress
  React.useEffect(() => {
    if (loading || logs?.[0]?.state === IntegrationSyncState.InProgress) {
      const interval = setInterval(() => getIntegration(), 1000);
      return () => clearInterval(interval);
    }
  }, [loading, logs?.[0]?.id, logs?.[0]?.state, lastSyncLogId]);

  // Update the current time every 5 seconds
  React.useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {logs.length > 0 && (
        <div className="flex flex-col gap-2 overflow-auto border border-neutral-200 dark:border-neutral-700 rounded-lg">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-900">
              <tr>
                <th
                  scope="col"
                  className="py-2 pr-3 pl-4 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-sm font-semibold text-neutral-900 dark:text-white"
                >
                  Result
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="py-4 pr-3 pl-4 text-sm whitespace-nowrap text-neutral-900 dark:text-white">
                    <div className="font-medium flex items-center gap-2">
                      {log.state === IntegrationSyncState.Completed && <CompletedIndicator />}
                      {log.state === IntegrationSyncState.InProgress && <InProgressIndicator />}
                      {log.state === IntegrationSyncState.Failed && <FailedIndicator />}
                      {titleCase(log.type)}
                    </div>
                    <div className="text-neutral-500 dark:text-neutral-400 text-xs mt-1 ml-[1.125rem]">
                      {relativeDate(now, new Date(log.startedAt))}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm whitespace-nowrap text-neutral-500 dark:text-neutral-400">
                    {log.lastError && (
                      <div className="text-red-600 dark:text-red-400 text-xs whitespace-break-spaces">
                        {log.lastError}
                      </div>
                    )}
                    {!log.lastError && log.state === IntegrationSyncState.Completed && (
                      <div className="text-neutral-600 dark:text-neutral-400 text-xs whitespace-break-spaces">
                        Successfully synced {log.entitiesSynced} entities
                      </div>
                    )}
                    {!log.lastError && log.state === IntegrationSyncState.InProgress && (
                      <div className="text-neutral-600 dark:text-neutral-400 text-xs whitespace-break-spaces">
                        Syncing in progress...
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!hideSyncButton && canSync && (
        <div className="mt-4">
          <PrimaryButton
            color="white"
            className="w-full"
            onClick={syncIntegration}
            loading={loading}
          >
            {integration.type === IntegrationType.CrmHubspot ? 'Save & Sync' : 'Sync Now'}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

const HubSpotTicketPipelineSelect = ({
  integrationId,
  value,
  onChange,
  disabled
}: {
  integrationId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  const { data, loading, error } = useGetHubSpotTicketPipelinesQuery({
    variables: { integrationId }
  });

  if (loading) return <Spinner size="size-4" color="neutral" />;
  if (error) return null;

  const pipelines = data?.hubspotTicketPipelines || [];
  const activePipelines = pipelines.filter((p) => !p.archived);
  const selectedPipeline = activePipelines.find((p) => p.id === value);

  return (
    <Combobox
      value={selectedPipeline?.label || ''}
      minQueryLength={0}
      maxOptions={1000}
      onChange={(label) => {
        const pipeline = activePipelines.find((p) => p.label === label);
        if (pipeline) {
          onChange(pipeline.id);
        } else {
          // Handle empty selection
          onChange('');
        }
      }}
      options={[
        { value: '', label: 'Select a pipeline...', description: '' },
        ...activePipelines
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((pipeline) => ({
            value: pipeline.label,
            label: pipeline.label,
            description: `${pipeline.stages.length} stages`
          }))
      ]}
      disabled={disabled}
    />
  );
};

const HubSpotTicketStageSelect = ({
  integrationId,
  pipelineId,
  value,
  onChange,
  disabled
}: {
  integrationId: string;
  pipelineId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  const { data, loading, error } = useGetHubSpotTicketPipelinesQuery({
    variables: { integrationId }
  });

  if (loading) return <Spinner size="size-4" color="neutral" />;
  if (error) return null;

  const pipeline = data?.hubspotTicketPipelines?.find((p) => !p.archived && p.id === pipelineId);
  if (!pipeline) return null;

  const stages = [...(pipeline.stages || [])]; // Create a copy of the array

  return (
    <select
      className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-neutral-900 dark:text-white ring-1 ring-inset ring-neutral-300 dark:ring-neutral-600 focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">Select a stage...</option>
      {stages
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((stage) => (
          <option key={stage.id} value={stage.id}>
            {stage.label}
          </option>
        ))}
    </select>
  );
};
